// The exploration engine (redesign v2). Expand-in-place source navigation - tap a
// source CARD and its text opens inline; the sources IT cites appear below as
// tappable cards (the rabbit hole) - wrapped in the "Torah Source Journey" chrome:
// a sticky ORIENTATION bar (anchor + how-deep + return routes), a JOURNEY drawer /
// desktop rail (the trail, "how did I get here", side-paths, summarize), honest
// relationship LABELS + source-type marks on every card, PEEK-before-dive, and
// BRANCH (save a side-path without derailing). Accuracy-lock preserved: only real
// refs returned this request render; only in-taxonomy sources show; relationship
// labels derive ONLY from real Sefaria fields; everything sanitized.
//
// ARCHITECTURE: the live DOM path (trailChain over the open .srow/.sbody tree) is
// the SINGLE source of truth for the linear trail, so replayPath() + the "~" hash
// deep-links keep working unchanged. The journey chrome is a READ-ONLY PROJECTION
// of that DOM path; only side-paths/peeked refs live in a thin DOM-free overlay.
//
// v7 NAVIGATION INVARIANTS: (1) ACCORDION - opening a row closes its open
// sibling (and any chain under another verse), so the open rows app-wide are
// always EXACTLY the route's one linear chain; (2) HISTORY - the navigator
// owns the URL: every user chain change is one history.pushState entry
// (navHistory.ts), restore-driven applies (cold replay / popstate diff-apply
// via applyRoute / side-path replay) are bracketed by beginRestore/endRestore
// and never push. Back/Forward therefore walk the journey like a browser -
// including re-opening a branch the accordion auto-closed.
//
// replayPath() SELECTOR CONTRACT (do not break - audited): replayPath walks, in
// order, `.verse[data-ref]` -> `:scope > .vactions > .srcbtn` + `:scope > .sources`
// -> `:scope > .srow[data-ref]` -> `:scope > .srowhead` -> `:scope > .sbody`
// -> `.cites .citelist`. Therefore: every .ec card class is added ONTO .srow (the
// .ec wrapper is NOT a new nesting level); .srowhead and .sbody stay DIRECT children
// of .srow; data-ref carries the commentatorVerseRef-stripped ref. data-linkType /
// data-category / data-he are also persisted so the journey projection (relationship
// labels, Hebrew names) reconstructs identically from a cold deep-link reload.
//
// FOLD CHANNELS (v5): four independent visibility layers, each its own attribute,
// none touching the DOM tree shape - `hidden` (more-fold + bundle members),
// `data-secfold` (verse accordion sections AND the rabbit-hole bucket disclosures
// - both via setSectionExpanded, which never folds an open trail row),
// `data-langhide` (the Show-sources language filter). revealRow() clears every
// layer before a row opens, so replay / Explore "Open" can never strand a target.
// The rabbit-hole buckets are COLLAPSED by default (v5 simplification): the
// center column is for reading; the right Explore panel (the "What connects
// here" type stats + category accordions) is the browse surface, projected
// from the same citeData.

import { el, button } from "../lib/dom.ts";
import { onIdle } from "../lib/idle.ts";
import { textBlock, errorRow } from "./textBlock.ts";
import { getLinks, getText, getTextForRecovery, getChapterLinks, findRefsInText, searchRefs, parseParashaRef, validCompDate, type SourceLink, type VerifiedText } from "../lib/sefaria.ts";
import { distributeChapterLinks, scentVisible, scentFamilies, scentBreakdown } from "../lib/terrain.ts";
import { acceptSourceLink, categoryRank } from "../lib/taxonomy.ts";
import {
  markExplored,
  listExplored,
  exploredCount,
  saveTrail,
  listTrails,
  updatePrefs,
  persistRailOpen,
  markVerseVisited,
  listVerseVisited,
  addBookmark,
  getBookmarks,
  removeBookmark,
  type BookmarkItem,
} from "../lib/store.ts";
import { getRelationship, strengthRank, type RelStrength } from "../lib/relationship.ts";
import { getSourceType, tanakhSection } from "../lib/typeSystem.ts";
import { parseCanonicalRef, lookupWork } from "../lib/normalizeRef.ts";
import { resolveQuotedRefs } from "../lib/quoteResolver.ts";
import { commentaryEra } from "../lib/era.ts";
import { prominenceRank } from "../lib/prominence.ts";
import { citedInText } from "../lib/citations.ts";
import {
  compactPath,
  dedupeSidePaths,
  type PathNode,
  type SidePath,
} from "../lib/journey.ts";
import { openPeek } from "./peek.ts";
import { skeletonLines } from "./skeleton.ts";
import { openModal } from "./modal.ts";
import { refFromSefariaUrl, refsOverlap } from "../lib/refUrl.ts";
import { buildRoute } from "../lib/route.ts";
import * as navHistory from "../lib/navHistory.ts";
import { trapFocus } from "../lib/focusTrap.ts";
import { syncOverlayBackground } from "../lib/overlayBackground.ts";
import { renderSourceCard, relChip, actBtn, openTip, deskTip, bookmarkTip, type SourceConnection } from "./sourceCard.ts";
import { sourceContextLine } from "../lib/sourceContext.ts";
import { describeSource } from "../lib/commentatorInfo.ts";
import { edgeFromBucket, type AdaptedConnection, type ConnectionBucket } from "../lib/sourceGraphAdapter.ts";
import { getTrustBadge, explainSourceConnection, type TrustTone } from "../lib/sourceTrust.ts";
import { GROUP_META } from "../lib/sourceGraphQuery.ts";
import { recordAdapted } from "../lib/sourceGraphSession.ts";
import { isSourceDebug } from "../lib/debugFlags.ts";
import { eraOf, type EraInfo } from "../lib/timeline.ts";
import {
  getSourceLangMode,
  setSourceLangMode,
  hasEnglishAvailable,
  hasHebrewAvailable,
  shouldShowSourceForDisplayMode,
  type SourceDisplayMode,
} from "../lib/sourceLang.ts";
import {
  addToDesk,
  getDeskItems,
  isOnDesk,
  loadDeskFromStore,
  subscribeDesk,
  type DeskItem,
} from "../lib/desk.ts";
import {
  closeDeskDrawer,
  openDeskDrawer,
  refreshDeskDrawer,
  renderDeskDock,
  setDeskDockOpen,
  showDeskToast,
  type SourceDeskActions,
} from "./sourceDesk.ts";
import {
  closeBookmarksDrawer,
  openBookmarksDrawer,
  refreshBookmarksDrawer,
  type BookmarkActions,
} from "./bookmarks.ts";
import { closeSavedPathsDrawer, openSavedPathsDrawer, refreshSavedPathsDrawer } from "./savedPaths.ts";

export interface NavHandle {
  orientationBar: HTMLElement;
  currentSourceCard: HTMLElement; // the "Current Source" identity card above the reader
  statline: HTMLElement;
  srStatus: HTMLElement; // the ONE aria-live region for position announcements
  parashaRef: string; // the parasha this navigator renders (app-shell warm-match + entry upgrades)
  attachVerse(verseEl: HTMLElement, ref: string): void;
  replayPath(path: string[], opts?: ReplayOpts): Promise<void>;
  // Warm history restore (v7): diff the live DOM chain against a target route
  // path - close what diverges, open what is missing - WITHOUT remounting the
  // reader. popstate traversals within the same parasha land here.
  applyRoute(target: string[], opts?: ApplyOpts): Promise<void>;
  // The "Show sources" display mode: set + persist + re-annotate every loaded
  // list (the reader control and the Show-all escape hatches both route here).
  applySourceLang(mode: SourceDisplayMode): void;
  // Re-render the Explore panel so its in-panel source reader re-reads the
  // reader's Translation toggle + font scale (the panel sits outside .reader).
  refreshDisplay(): void;
}

export interface ReplayOpts {
  // Push ONE history entry for the achieved route when the walk completes
  // (side-path jumps); replay-driven setTrail calls themselves never push.
  push?: boolean;
  // Latest-wins cancellation: checked between awaits so a rapid second
  // traversal can abandon an in-flight walk.
  cancelled?: () => boolean;
}

export interface ApplyOpts {
  cancelled?: () => boolean;
  direction?: "back" | "forward"; // for the landing announcement
}

// Test-only (pattern: sefaria._resetCaches): the scent layer's auto-start is
// suppressed under vitest; the scent dom test opts back in through this.
let TERRAIN_IN_TESTS = false;
export function _enableTerrainForTests(): void {
  TERRAIN_IN_TESTS = true;
}

// Same pattern for the find-refs text enhancement: inert under vitest unless
// the findrefs dom test opts in, so the other dom suites' fetch routers never
// see stray POSTs.
let FINDREFS_IN_TESTS = false;
export function _enableFindRefsForTests(): void {
  FINDREFS_IN_TESTS = true;
}

// Same for the verbatim-quote recovery layer: inert under vitest unless the
// recovery dom test opts in, so the broad cited===0 gate never fires stray
// search POSTs (or promises that outlive teardown) in the other dom suites.
let RECOVER_IN_TESTS = false;
export function _enableQuoteRecoveryForTests(): void {
  RECOVER_IN_TESTS = true;
}

// ---------------------------------------------------------------------------
// v7 restore bracket + walk ownership (MODULE level, deliberately).
//
// The navigator OWNS the URL: every user-driven chain change becomes a REAL
// history entry (navHistory.pushRoute). Restore-driven applies (cold replay,
// side-path replay, popstate diff-apply) run inside beginRestore/endRestore:
// their setTrail calls render chrome but never push - the bracket owner
// pushes/replaces at most once at the end. The depth COUNTER (not a boolean)
// exists for CONCURRENT overlap, not nesting: an un-awaited side-path replay
// can race a popstate diff-apply, and the data-mm-replay motion gate must
// stay down until the LAST walk finishes. Module level (not per-navigator)
// because the gate attribute and the history stack are document-global - a
// route remount must not let a stale instance's endRestore lift the gate the
// new instance's bracket just set.
//
// walkSeq makes walks latest-wins ACROSS instances: every applyRoute /
// replayPath invocation (and every createNavigator mount) bumps it, and an
// older in-flight walk self-cancels at its next await - an orphaned cold
// replay can never keep clicking rows after a remount or a newer traversal.
let restoreDepth = 0;
let walkSeq = 0;
let overlayOwnerSeq = 0;
// Latest-wins walk token. ++walkSeq claims the newest walk; the returned cancel()
// reports true once a NEWER walk/applyRoute/mount has bumped walkSeq past this one, or
// the caller's own cancelled() fires. Every async walk MUST re-check it after each
// await so a superseded or detached instance can never keep clicking rows or push a
// wrong-parasha history entry (review H2: make the contract an API, not an idiom).
function beginWalk(callerCancelled?: () => boolean): () => boolean {
  const mine = ++walkSeq;
  return (): boolean => walkSeq !== mine || (callerCancelled?.() ?? false);
}
const restoring = (): boolean => restoreDepth > 0;
function beginRestore(): void {
  restoreDepth++;
  // The walk drives REAL click handlers - the attribute lets the stylesheet
  // hold all motion until the journey has settled (replay gate, T6).
  document.documentElement.setAttribute("data-mm-replay", "");
}
function endRestore(): void {
  restoreDepth = Math.max(0, restoreDepth - 1);
  if (restoreDepth) return;
  document.documentElement.removeAttribute("data-mm-replay");
  // Un-matching the gate rules RESTARTS the suppressed CSS animations
  // (Chromium recreates an animation whenever its computed animation-*
  // changes, verified live: 9 cardin + 1 crumbpulse fired at gate-lift).
  // Finish them in this same frame, before paint, so the landing is
  // genuinely settled. skelsweep is infinite (finish() would throw) and
  // must keep running - filter by name, never finish() blindly.
  const settled = ["cardin", "crumbpulse", "justopen"];
  for (const a of document.getAnimations?.() ?? []) {
    if (a instanceof CSSAnimation && settled.includes(a.animationName)) a.finish();
  }
}

// --orient-h re-publisher. Navigator instances are recreated per route, so a
// per-instance window listener would leak; the ONE module-level listener reads
// whichever orientation bar is currently live and re-measures it when the
// window resizes (the bar wraps/unwraps without any trail move).
let liveOrientBar: HTMLElement | null = null;
let orientResizeWired = false;
// --topbar-h is a constant CSS token (changes only at a viewport breakpoint); parse it
// once and re-read lazily instead of a getComputedStyle parse on every reveal() (the
// hot row-open path). The resize listener below clears it.
let topbarHCache: number | null = null;
function getTopbarH(): number {
  if (typeof window === "undefined") return 53;
  if (topbarHCache == null) {
    topbarHCache = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--topbar-h")) || 53;
  }
  return topbarHCache;
}
function publishOrientHeightFor(bar: HTMLElement): void {
  const h = bar.hidden ? 0 : bar.offsetHeight;
  document.documentElement.style.setProperty("--orient-h", `${h}px`);
}
function wireOrientResize(): void {
  if (orientResizeWired || typeof window === "undefined") return;
  orientResizeWired = true;
  // Coalesce a burst of resize events into ONE layout read per frame. On mobile the
  // URL bar showing/hiding during scroll fires resize, so an unthrottled offsetHeight
  // read here forces a reflow mid-scroll (jank). rAF batches it to one read/frame.
  let resizeRaf = 0;
  window.addEventListener("resize", () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      topbarHCache = null; // a breakpoint may change --topbar-h
      if (liveOrientBar?.isConnected) publishOrientHeightFor(liveOrientBar);
    });
  });
}

// ONE availability-merge rule for same-source segments, shared by groupLinks
// (panel rows) AND the scent layer's parity dedupe - the two counts must stay
// equal by construction, never by parallel maintenance: any true -> true; all
// false -> false; otherwise unknown (never hide on a guess).
function mergeAvail(a: boolean | null | undefined, b: boolean | null | undefined): boolean | null {
  if (a === true || b === true) return true;
  if (a === false && b === false) return false;
  return null;
}

// Map an internal relationship strength tier to the SourceCard's coarse strength.
function strengthChip(s: "direct" | "related" | "thematic"): "strong" | "medium" | "weak" {
  return s === "direct" ? "strong" : s === "related" ? "medium" : "weak";
}

// Display projection for JOURNEY CHROME chips (current-source header, journey
// map, back rows, Compare). The relationship layer's labels stay untouched -
// this only rewords the one phrase the learner UX bans ("comments on this",
// internal-sounding and redundant on cards) to a calm noun. Source CARDS show
// no relationship chips at all outside debug; chrome keeps a chip because the
// journey genuinely needs the orientation.
function relDisplay(label: string): string {
  return label === "comments on this" ? "commentary" : label;
}

// The user-facing trust tiers behind the Explore filter. Tones collapse into
// three honest buckets: verified > probable > suggested (warning sorts with
// suggested so a flagged/evidence-less edge is QUIETER, never louder).
type TrustTier = "verified" | "probable" | "suggested";
function tierForTone(tone: TrustTone): TrustTier {
  if (tone === "verified") return "verified";
  if (tone === "suggested" || tone === "warning") return "suggested";
  return "probable"; // probable + neutral
}

// Human phrasing for the Why? modal's "source of connection" line.
function originLabel(adapted: AdaptedConnection): string {
  const p = adapted.edge.provenance;
  switch (p.origin) {
    case "provider_import":
      return "Imported from the text provider's link index";
    case "algorithm_detected":
      return p.algorithm ? `Detected by the ${p.algorithm.name} parser (deterministic, not AI)` : "Detected by a deterministic parser";
    case "manual_entry":
    case "admin_created":
      return "Entered by an editor";
    case "ai_suggested":
      return "Suggested by AI - not a verified citation";
    case "user_created":
      return "Created by you";
  }
}

// How deep the cited-source rabbit hole auto-loads before requiring an extra tap.
const MAX_AUTO_DEPTH = 6;

// Named timing literals (ms). Grouped so a tuner edits one place, not scattered
// setTimeout / waitFor / onIdle call sites.
const JUST_OPENED_MS = 1300; // how long a freshly-opened row keeps its "just-opened" flash
const WAITFOR_DEFAULT_MS = 8000; // default ceiling for waitFor() polling
const ANNOUNCE_DEBOUNCE_MS = 150; // aria-live announce coalescing window
const CITELIST_WAIT_MS = 5000; // replay: wait for a level's citelist before descending
const SCENT_IDLE_FALLBACK_MS = 1500; // scent layer idle-callback fallback timer
const FINDREFS_IDLE_FALLBACK_MS = 800; // find-refs enhancement idle-callback fallback timer

let uid = 0;
const nextId = (prefix: string): string => `${prefix}-${(uid += 1)}`;

// Session memory for the verse-panel section ACCORDIONS, keyed
// `${anchorRef}::${label}` -> open. Module-level so a reader's open/closed
// choices survive view re-mounts within one app session; deliberately NOT
// persisted (a fresh session starts at the calm default: first section open).
const sectionMemory = new Map<string, boolean>();
export function _resetSectionMemory(): void {
  sectionMemory.clear();
}

interface Item {
  commentator: string;
  ref: string;
  commentatorHe: string | null;
  linkType: string; // threaded through so the row dataset (and thus the deep-link replay) keeps it
  category: string;
  // Language availability of the source's text (merged across the segment-links
  // groupLinks dedupes into this Item; see mergeAvail). null = unknown, and the
  // display-mode filter never hides on unknown. Optional so pure-helper tests
  // (sectionizeCommentary etc) can omit them.
  hasEn?: boolean | null;
  hasHe?: boolean | null;
  // Era/time-hop inputs carried through from the source link (real value or absent;
  // optional so pure-helper tests can omit them). Feeds the trail spine (timeline.ts).
  compDate?: [number] | [number, number] | null;
  era?: string | null;
}

const prefersReduced = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const isDesktop = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia?.("(min-width: 960px)").matches;

// Scroll an opened panel into view and flash it, so a tap is never silent. Honors
// reduced-motion (programmatic scroll too). No-ops safely under jsdom/tests.
function reveal(target: HTMLElement): void {
  const r = target.getBoundingClientRect?.();
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;
  const topbarH = getTopbarH();
  const offView = !r || r.top < topbarH || (vh > 0 && r.bottom > vh);
  if (offView) target.scrollIntoView?.({ behavior: prefersReduced() ? "auto" : "smooth", block: "center" });
  target.classList.add("just-opened");
  setTimeout(() => target.classList.remove("just-opened"), JUST_OPENED_MS);
}

function waitFor(pred: () => boolean, timeoutMs = WAITFOR_DEFAULT_MS): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = (): void => {
      if (pred() || Date.now() - start > timeoutMs) return resolve();
      setTimeout(tick, 60);
    };
    tick();
  });
}

// Map a [start, end) char range over root's textContent to the single Text
// node that contains it. A range that crosses node boundaries returns null -
// the caller skips rather than half-wrapping (find-refs offsets are global
// over the submitted string; wrapping preserves total text, so earlier
// global offsets stay valid after a wrap).
export function findTextSpan(
  root: HTMLElement,
  start: number,
  end: number,
): { node: Text; startOff: number; endOff: number } | null {
  if (end <= start || start < 0) return null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let pos = 0;
  let n = walker.nextNode();
  while (n) {
    const t = n as Text;
    const len = t.data.length;
    if (start < pos + len) {
      if (end > pos + len) return null; // crosses into the next node
      return { node: t, startOff: start - pos, endOff: end - pos };
    }
    pos += len;
    n = walker.nextNode();
  }
  return null;
}


// Stat key for one explore item: the typeSystem bucket, with Tanakh refined to
// its section by the book title parsed from the item's own canonical ref.
export function statKeyFor(it: { ref: string; category: string }): string {
  const t = getSourceType(it.category);
  if (t.key !== "tanakh") return t.key;
  const work = parseCanonicalRef(it.ref)?.workTitle;
  const sec = work ? tanakhSection(work) : null;
  return sec ? `tanakh-${sec.key}` : "tanakh";
}



// The verse-mode Explore HERO: a compact row of honest stat tiles shown on top
// the moment a verse is selected - how many KINDS of source, how many are direct
// commentary (meforshim), and the grand total. "Categories" counts the SAME
// section rows the accordion below renders (sectionizeGroups - so commentary's
// Classic/Later split is reflected), so the headline always equals what the
// learner can actually open. All counts come from the already-filtered `visible`
// links. (Restored 2026-06-19; was dropped in 878c78a's reading-model simplify.)
function buildVerseHero(visible: readonly SourceLink[]): HTMLElement {
  const total = visible.length;
  const categories = sectionizeGroups(groupLinks([...visible])).length;
  let commentaries = 0;
  for (const l of visible) if (getSourceType(l.category).key === "commentary") commentaries++;

  const hero = el("div", "explore-verse-total");
  const stats = el("div", "vhero-stats");
  const tile = (n: number, label: string): HTMLElement => {
    const t = el("div", "vhero-stat");
    t.append(el("span", "vhero-stat-n", String(n)), el("span", "vhero-stat-l", label));
    return t;
  };
  // structure -> subset -> whole: the kinds of source, then how many are
  // commentary, ending on the grand total (which caps the category list below).
  stats.append(tile(categories, categories === 1 ? "Category" : "Categories"));
  if (commentaries > 0) {
    stats.append(tile(commentaries, commentaries === 1 ? "Commentary" : "Commentaries"));
  }
  stats.append(tile(total, total === 1 ? "Source" : "Sources"));
  hero.append(stats);
  return hero;
}

const attrEsc = (s: string): string => s.replace(/["\\]/g, "\\$&");

export function createNavigator(
  rootLabel: string,
  parashaRef: string,
  onPathChange?: (path: string[]) => void,
): NavHandle {
  // A new mount invalidates any in-flight walk from a previous instance (the
  // orphaned walk would otherwise keep clicking rows in the NEW DOM).
  walkSeq++;
  const overlayOwner = ++overlayOwnerSeq;
  const overlaysActive = (): boolean => overlayOwner === overlayOwnerSeq;
  // ---- the orientation bar (the one sticky journey line) -------------------
  const orientationBar = el("nav", "orientbar");
  orientationBar.setAttribute("aria-label", "Where you are in your journey");
  orientationBar.hidden = true;

  // ---- the "Current Source" identity card (mounted above the reader) --------
  // A read-only projection of the deepest open source: what you are reading now,
  // where it opened from, the relationship, and how deep you are - with escape
  // hatches (Back / Compare to Start / Return to Start). Hidden at depth 0.
  const currentSourceCard = el("section", "csource");
  currentSourceCard.setAttribute("aria-label", "Current source");
  currentSourceCard.hidden = true;

  // Per-node connected-source data captured when a source's cites load, so the
  // right-side "Explore Next" panel projects the SAME honest buckets the inline
  // rabbit-hole used (no second fetch, no DOM re-parsing). Keyed by the open .srow.
  interface CiteData {
    cited: Item[];
    commentaryOn: Item[];
    otherConnected: Item[];
  }
  const citeData = new WeakMap<HTMLElement, CiteData>();

  // ---- the one aria-live region (position announcements, debounced) --------
  const srStatus = el("div", "sr-status");
  srStatus.setAttribute("aria-live", "polite");
  let announceTimer: ReturnType<typeof setTimeout> | null = null;
  function announce(msg: string): void {
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(() => {
      srStatus.textContent = msg;
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  // ---- the explored stat line (static, NOT a live region) ------------
  const statline = el("div", "statline");
  async function refreshStats(): Promise<void> {
    try {
      const n = await exploredCount();
      statline.textContent = `${n} ${n === 1 ? "source" : "sources"} explored`;
    } catch {
      /* leave the last value on a transient store error */
    }
  }
  void refreshStats();

  // ---- Source Desk + Bookmarks: additive overlays, never route/trail truth ---
  let bookmarkedRefs = new Set<string>();
  let bookmarkCount = 0;
  // Tracks the last header-rendered Bookmarks count so the badge "pops" only on
  // the 0 -> N transition (first bookmark / first load), not on every change.
  let prevHeaderBookmarkCount = 0;

  const deskActions = (): SourceDeskActions => ({
    onOpenItem: (item) => openRefFromOverlay(item.ref),
    onBookmark: (item, btn) => void bookmarkIdentity(toBookmarkItem(item), btn),
    announce,
  });

  const bookmarkActions = (): BookmarkActions => ({
    onOpen: (item) => openRefFromOverlay(item.ref),
    onPreview: (item) => previewIdentity(item.ref, item.enTitle, item.heTitle, item.category, "bookmarked source"),
    onRemove: async (item) => {
      await removeBookmark(item.ref);
      bookmarkedRefs.delete(item.ref);
      bookmarkCount = bookmarkedRefs.size;
      refreshBookmarkSurfaces();
      showDeskToast("Removed from Bookmarks.", undefined, undefined, announce);
    },
  });

  let unsubscribeDesk: (() => void) | null = null;
  function renderDeskIfActive(): void {
    if (!overlaysActive()) {
      unsubscribeDesk?.();
      unsubscribeDesk = null;
      return;
    }
    renderDeskSurfaces();
  }

  void loadDeskFromStore().then(renderDeskIfActive);
  unsubscribeDesk = subscribeDesk(renderDeskIfActive);
  void loadBookmarkRefs();

  async function loadBookmarkRefs(): Promise<void> {
    try {
      const bookmarks = await getBookmarks();
      if (!overlaysActive()) return;
      bookmarkedRefs = new Set(bookmarks.map((b) => b.ref));
      bookmarkCount = bookmarkedRefs.size;
      refreshBookmarkSurfaces();
    } catch {
      /* bookmarks stay empty on a transient store error */
    }
  }

  function renderDeskSurfaces(): void {
    renderDeskDock(deskActions());
    refreshDeskDrawer();
    refreshInlineOverlayButtons();
    renderBottomNav();
  }

  function refreshBookmarkSurfaces(): void {
    refreshBookmarksDrawer();
    refreshInlineOverlayButtons();
    refreshExplore();
    renderBottomNav();
  }

  function toDeskItem(item: Item): DeskItem {
    return {
      ref: item.ref,
      enTitle: itemLabel(item),
      heTitle: item.commentatorHe,
      category: item.category,
      addedAt: Date.now(),
    };
  }

  function connToDeskItem(c: SourceConnection): DeskItem {
    return {
      ref: c.id,
      enTitle: c.title,
      heTitle: c.hebrewTitle ?? null,
      category: c.category ?? "Commentary",
      addedAt: Date.now(),
    };
  }

  function toBookmarkItem(item: DeskItem | Item | SourceConnection): BookmarkItem {
    if ("enTitle" in item) {
      return {
        ref: item.ref,
        enTitle: item.enTitle,
        heTitle: item.heTitle,
        category: item.category,
        addedAt: Date.now(),
      };
    }
    if ("id" in item) {
      return {
        ref: item.id,
        enTitle: item.title,
        heTitle: item.hebrewTitle ?? null,
        category: item.category ?? "Commentary",
        addedAt: Date.now(),
      };
    }
    return {
      ref: item.ref,
      enTitle: itemLabel(item),
      heTitle: item.commentatorHe,
      category: item.category,
      addedAt: Date.now(),
    };
  }

  function markDeskButton(btn: HTMLButtonElement, label: string): void {
    setDeskButton(btn, label, true);
  }

  function setDeskButton(btn: HTMLButtonElement, label: string, onDesk: boolean): void {
    if (!onDesk) {
      btn.textContent = "Add to Desk";
      btn.classList.remove("kept");
      btn.setAttribute("aria-label", deskTip(label));
      btn.title = deskTip(label);
      return;
    }
    btn.textContent = "On desk";
    btn.classList.add("kept");
    btn.setAttribute("aria-label", `${label} is on the Source Desk`);
    btn.title = `${label} is on the Source Desk`;
  }

  function markBookmarkButton(btn: HTMLButtonElement, label: string): void {
    setBookmarkButton(btn, label, true);
  }

  function setBookmarkButton(btn: HTMLButtonElement, label: string, bookmarked: boolean): void {
    const iconOnly = btn.classList.contains("desk-bookmark") || btn.classList.contains("xcard-bookmark");
    if (!bookmarked) {
      if (!iconOnly) btn.textContent = "Bookmark";
      btn.classList.remove("kept");
      btn.setAttribute("aria-pressed", "false");
      btn.setAttribute("aria-label", bookmarkTip(label));
      btn.title = bookmarkTip(label);
      return;
    }
    if (!iconOnly) btn.textContent = "Bookmarked";
    btn.classList.add("kept");
    btn.setAttribute("aria-pressed", "true");
    btn.setAttribute("aria-label", `${label} is bookmarked`);
    btn.title = `${label} is bookmarked`;
  }

  function rowLabel(row: HTMLElement): string {
    return row.dataset.label ?? row.querySelector(":scope > .srowhead .ec-en")?.textContent ?? row.dataset.ref ?? "source";
  }

  function refreshInlineOverlayButtons(ref?: string): void {
    const scope = ref ? `.srow[data-ref="${attrEsc(ref)}"]` : ".srow[data-ref]";
    for (const row of document.querySelectorAll(scope)) {
      if (!(row instanceof HTMLElement)) continue;
      const label = rowLabel(row);
      const deskBtn = row.querySelector(":scope > .ec-acts .ec-desk");
      if (deskBtn instanceof HTMLButtonElement) setDeskButton(deskBtn, label, isOnDesk(row.dataset.ref ?? ""));
      const bookmarkBtn = row.querySelector(":scope > .ec-acts .ec-bookmark");
      if (bookmarkBtn instanceof HTMLButtonElement) {
        setBookmarkButton(bookmarkBtn, label, bookmarkedRefs.has(row.dataset.ref ?? ""));
      }
    }
  }

  function addDeskIdentity(item: DeskItem, btn?: HTMLButtonElement): void {
    const result = addToDesk(item);
    if (result === "full") {
      showDeskToast(
        "Desk full. Close one to add another.",
        "Bookmark instead?",
        () => void bookmarkIdentity(toBookmarkItem(item)),
        announce,
      );
      return;
    }
    if (btn) markDeskButton(btn, item.enTitle);
    setDeskDockOpen(false);
    renderDeskSurfaces();
    const msg = result === "added" ? "Added to Desk." : "Already on Desk.";
    showDeskToast(msg, "Open Desk", () => {
      openDeskSurface();
    }, announce);
  }

  // Bookmark buttons TOGGLE: clicking an already-bookmarked source removes it,
  // otherwise it is added. Shared by every bookmark affordance (explore cards,
  // inline cards, peek, desk), so un-bookmarking works the same everywhere.
  async function bookmarkIdentity(item: BookmarkItem, btn?: HTMLButtonElement): Promise<void> {
    if (bookmarkedRefs.has(item.ref)) {
      await removeBookmark(item.ref);
      bookmarkedRefs.delete(item.ref);
      bookmarkCount = bookmarkedRefs.size;
      if (btn) setBookmarkButton(btn, item.enTitle, false);
      refreshBookmarkSurfaces();
      refreshInlineOverlayButtons(item.ref);
      showDeskToast("Removed from Bookmarks.", "Open Bookmarks", () => {
        openBookmarksSurface();
      }, announce);
      return;
    }
    await addBookmark(item);
    bookmarkedRefs.add(item.ref);
    bookmarkCount = bookmarkedRefs.size;
    if (btn) markBookmarkButton(btn, item.enTitle);
    refreshBookmarkSurfaces();
    refreshInlineOverlayButtons(item.ref);
    showDeskToast("Added to Bookmarks.", "Open Bookmarks", () => {
      openBookmarksSurface();
    }, announce);
  }

  function previewIdentity(ref: string, label: string, he: string | null, category: string, relationshipLabel: string): void {
    const type = getSourceType(category);
    openPeek({
      ref,
      label,
      labelHe: he,
      relationshipLabel,
      colorVar: type.colorVar,
      onBookmark: (btn) => void bookmarkIdentity({ ref, enTitle: label, heTitle: he, category, addedAt: Date.now() }, btn),
      bookmarked: bookmarkedRefs.has(ref),
    });
  }

  function openRefFromOverlay(ref: string): boolean {
    const opened = withTrailSurface("overlay", () => continueByRef(ref));
    if (opened) {
      closeDeskDrawer(false);
      closeBookmarksDrawer(false);
      closeSavedPathsDrawer(false);
    }
    return opened;
  }

  function openDeskSurface(): void {
    closeJourneyDrawer();
    closeExploreDrawer();
    closeBookmarksDrawer(false);
    closeSavedPathsDrawer(false);
    if (isDesktop()) {
      setDeskDockOpen(true);
      renderDeskSurfaces();
      return;
    }
    openDeskDrawer(deskActions(), buildDeskVerseHead());
  }

  // The mobile Desk drawer pins the currently-selected pasuk at the top so you
  // can compare sources WHILE seeing the verse (desktop keeps it visible above
  // the fixed dock; mobile's drawer would otherwise hide it). Read-only mirror of
  // the already-rendered, already-validated verse (ACCURACY-LOCK safe - no
  // re-fetch): clone its .vhe/.ven and strip every interactive hook so the copy
  // cannot pollute the drawer's focus trap or duplicate element ids. Honors the
  // reader's Translation toggle (omits English when it is off).
  function buildDeskVerseHead(): HTMLElement | null {
    if (!selectedVerseEl || !selectedVerseRef) return null;
    const pane = el("div", "desk-verse");
    pane.setAttribute("role", "group");
    pane.setAttribute("aria-label", `Selected verse, ${verseShort(selectedVerseRef)}`);
    pane.append(el("div", "desk-verse-ref", verseShort(selectedVerseRef)));
    const scroll = el("div", "desk-verse-scroll");
    const hideTrans = selectedVerseEl.closest(".reader")?.classList.contains("hide-trans") ?? false;
    for (const child of selectedVerseEl.querySelectorAll<HTMLElement>(":scope > .vhe, :scope > .ven")) {
      if (hideTrans && child.classList.contains("ven")) continue;
      const clone = child.cloneNode(true) as HTMLElement;
      sanitizeVerseClone(clone);
      scroll.append(clone);
    }
    pane.append(scroll);
    return pane;
  }

  // Make a cloned verse fragment inert: drop the select-target hooks, ids (no
  // duplicates in the DOM), and any focusable/labelled attributes so it cannot
  // enter the drawer's focus trap or double-speak to a screen reader.
  function sanitizeVerseClone(node: HTMLElement): void {
    for (const n of [node, ...node.querySelectorAll<HTMLElement>("*")]) {
      n.classList.remove("verse-select-target", "verse-select-target-primary");
      for (const attr of ["id", "tabindex", "role", "aria-pressed", "aria-label", "aria-controls"]) {
        n.removeAttribute(attr);
      }
    }
  }

  function openBookmarksSurface(): void {
    closeJourneyDrawer();
    closeExploreDrawer();
    closeDeskDrawer(false);
    closeSavedPathsDrawer(false);
    openBookmarksDrawer(bookmarkActions());
  }

  function openSavedPathsSurface(): void {
    closeJourneyDrawer();
    closeExploreDrawer();
    closeDeskDrawer(false);
    closeBookmarksDrawer(false);
    openSavedPathsDrawer();
  }

  function maybeNudgeDesk(): void {
    if (sessionOpened.size !== 2) return;
    const key = "mm:desk:nudge:v1";
    try {
      if (localStorage.getItem(key) === "1") return;
      localStorage.setItem(key, "1");
    } catch {
      /* a private-mode storage failure only skips persistence of the nudge */
    }
    showDeskToast("Source Desk can hold sources side by side.", "Open Desk", openDeskSurface, announce);
  }

  // ---- side-paths overlay (the only DOM-free journey state) ----------------
  let sidePaths: SidePath[] = [];
  void loadSidePaths();
  async function loadSidePaths(): Promise<void> {
    try {
      const trails = await listTrails();
      const mine: SidePath[] = [];
      for (const t of trails) {
        if (t.name !== `side:${parashaRef}`) continue;
        const d = t.data;
        if (isSidePath(d)) mine.push(d);
      }
      sidePaths = dedupeSidePaths(mine);
      refreshJourney();
    } catch {
      /* no side-paths on a transient store error */
    }
  }

  // ---- track the active chain (the last node toggled) ----------------------
  type SessionTrailSurface = "center" | "right-rail" | "overlay";

  interface SessionTrailEntry {
    seq: number;
    ref: string;
    label: string;
    heTitle: string | null;
    category: string;
    relationshipLabel: string;
    anchorRef: string;
    anchorLabel: string;
    path: string[];
    surface: SessionTrailSurface;
    openedAt: number;
  }

  let currentNode: HTMLElement | null = null;
  let selectedVerseRef: string | null = null;
  let selectedVerseEl: HTMLElement | null = null;
  // The pasuk a deep chain is rooted at. Reading a SOURCE clears selectedVerse
  // (so the panel leaves verse-mode), but the learner must never lose sight of
  // which verse they are under - so the anchor verse stays visibly marked via a
  // SEPARATE class from verse-selected (kept in sync by setAnchorVerse).
  let anchorVerseEl: HTMLElement | null = null;
  let terrainFailed = false;
  const scentChaptersLoaded = new Set<number>();
  let sessionTrail: SessionTrailEntry[] = [];
  let sessionTrailSeq = 0;
  let currentSessionTrailSeq: number | null = null;
  let pendingTrailSurface: SessionTrailSurface = "center";

  // Last rendered orientation depth, so renderOrientation can pulse the new
  // crumb ONLY when the trail deepened (never on back/jump re-renders).
  let lastOrientDepth = 0;

  // Session-recap tally (this parasha render): distinct sources opened.
  // Display-only closure state for the recap line - never persisted, never
  // trail truth.
  const sessionOpened = new Set<string>();

  // Footprints (Tranche 2): quiet cross-session memory. Both sets load async
  // once per navigator; until they land the marks are simply absent (memory is
  // an enhancement, never a blocker). exploredSet marks SOURCES ("seen"),
  // verseVisited marks PESUKIM (the dot by the verse number).
  let exploredSet = new Set<string>();
  void listExplored()
    .then((refs) => {
      exploredSet = new Set(refs);
    })
    .catch(() => {});
  const verseVisitedReady: Promise<Set<string>> = listVerseVisited()
    .then((refs) => new Set(refs))
    .catch(() => new Set<string>());

  // The tiny footprint dot beside the verse number. Idempotent; rides INSIDE
  // .vref (outside the replay selector chain). Text-in-aria, color never alone.
  function addFootprint(verseEl: HTMLElement): void {
    const vref = verseEl.querySelector(":scope > .vref");
    if (!(vref instanceof HTMLElement) || vref.querySelector(".vfoot")) return;
    const dot = el("span", "vfoot");
    dot.setAttribute("role", "img");
    dot.setAttribute("aria-label", "You explored sources here before");
    dot.title = "You explored sources here before";
    vref.append(dot);
  }

  function verseChapter(ref: string): number | null {
    const m = ref.match(/ (\d+):\d+$/);
    return m ? +m[1] : null;
  }

  function versePrimaryTarget(verseEl: HTMLElement): HTMLElement | null {
    return verseEl.querySelector(":scope > .vhe, :scope > .ven");
  }

  function setVerseSelectedState(verseEl: HTMLElement, selected: boolean): void {
    verseEl.classList.toggle("verse-selected", selected);
    const primary = versePrimaryTarget(verseEl);
    if (primary) primary.setAttribute("aria-pressed", String(selected));
  }

  function clearSelectedVerse(refresh = true): void {
    if (selectedVerseEl) setVerseSelectedState(selectedVerseEl, false);
    selectedVerseEl = null;
    selectedVerseRef = null;
    if (refresh) refreshExplore();
  }

  function selectVerse(verseEl: HTMLElement, ref: string): void {
    // Entering verse-mode: the selected-verse highlight supersedes any anchor
    // mark left over from a chain we were just reading under.
    setAnchorVerse(null);
    if (selectedVerseEl === verseEl && selectedVerseRef === ref) {
      refreshExplore();
      return;
    }
    if (selectedVerseEl) setVerseSelectedState(selectedVerseEl, false);
    selectedVerseEl = verseEl;
    selectedVerseRef = ref;
    setVerseSelectedState(verseEl, true);
    refreshExplore();
    renderBottomNav(); // surface the Explore drawer entry on mobile once a verse is picked
  }

  // Keep the pasuk that roots the open chain visibly marked while the learner
  // reads a SOURCE under it (reading-mode clears selectedVerse, so without this
  // the verse you came from loses its highlight - the "selected verse doesn't
  // stay highlighted" glitch). Idempotent; shares the verse-selected visual.
  function setAnchorVerse(node: HTMLElement | null): void {
    const verse = node ? trailChain(node).verse : null;
    const next = verse instanceof HTMLElement ? verse : null;
    if (anchorVerseEl === next) return;
    if (anchorVerseEl) anchorVerseEl.classList.remove("verse-anchored");
    anchorVerseEl = next;
    if (anchorVerseEl) anchorVerseEl.classList.add("verse-anchored");
  }

  function pathForNode(node: HTMLElement | null): string[] {
    const { verse, rows } = trailChain(node);
    return rows.length ? [verse?.dataset.ref ?? "", ...rows.map((r) => r.dataset.ref ?? "")] : [];
  }

  // The live chain as route segments: [verseRef, rowRef, ...] or [].
  function chainRefs(): string[] {
    return pathForNode(currentNode);
  }

  function trailAnchorForNode(node: HTMLElement | null): { anchorRef: string; anchorLabel: string } {
    const { verse } = trailChain(node);
    const anchorRef = verse?.dataset.ref ?? selectedVerseRef ?? "";
    return { anchorRef, anchorLabel: anchorRef ? verseShort(anchorRef) : rootLabel };
  }

  function appendSessionTrail(entry: Omit<SessionTrailEntry, "seq" | "openedAt">, refresh = true): SessionTrailEntry {
    const full: SessionTrailEntry = { ...entry, seq: ++sessionTrailSeq, openedAt: Date.now() };
    sessionTrail.push(full);
    currentSessionTrailSeq = full.seq;
    if (refresh) {
      refreshJourney();
      renderBottomNav();
    }
    return full;
  }

  function appendSessionTrailFromRow(row: HTMLElement, surface: SessionTrailSurface, refresh = true): void {
    const { anchorRef, anchorLabel } = trailAnchorForNode(row);
    const path = pathForNode(row);
    const depth = Math.max(1, path.length - 1);
    const rel = trailRel(row, depth === 1);
    appendSessionTrail(
      {
        ref: row.dataset.ref ?? "",
        label: row.dataset.label ?? "source",
        heTitle: row.dataset.he || null,
        category: row.dataset.category ?? "Commentary",
        relationshipLabel: relDisplay(rel.label),
        anchorRef,
        anchorLabel,
        path,
        surface,
      },
      refresh,
    );
  }

  function withTrailSurface<T>(surface: SessionTrailSurface, fn: () => T): T {
    const previous = pendingTrailSurface;
    pendingTrailSurface = surface;
    try {
      return fn();
    } finally {
      pendingTrailSurface = previous;
    }
  }

  function clearSessionTrail(refresh = true): void {
    sessionTrail = [];
    currentSessionTrailSeq = null;
    saveChoiceOpen = false;
    if (refresh) {
      refreshJourney();
      renderBottomNav();
    }
  }

  // When the in-app Back falls back to the structural backOne (a cold
  // deep-linked visitor standing on entry 0), the resulting trail write must
  // REWRITE the current entry, never push - a push would park the deep route
  // one entry below and the next Back would re-open it (review finding:
  // depth ping-pong, the user could never unwind).
  let replaceNextTrailWrite = false;

  function notifyPath(node: HTMLElement | null): void {
    currentNode = node; // chainRefs reads currentNode - keep in lockstep
    const path = chainRefs();
    onPathChange?.(path);
    if (!restoring()) {
      const route = buildRoute(parashaRef, path);
      if (replaceNextTrailWrite) {
        replaceNextTrailWrite = false;
        navHistory.replaceRoute(route);
      } else {
        navHistory.pushRoute(route);
      }
    }
  }

  // Rail-rebuild coalescing for replay walks (perf). refreshJourney/refreshExplore
  // each replaceChildren() a whole rail subtree, and a depth-D replay/applyRoute walk
  // triggers many of them - one per opened row (setTrail) PLUS each async citelist
  // load that lights up "Explore Next" as the level resolves. chromeWalk is held up
  // around each instance walk (replayPath + applyRoute, which can overlap on one
  // instance); while it is up, EVERY refreshJourney/refreshExplore call just marks its
  // rail dirty, and the walk flushes ONE rebuild per rail when the last overlapping
  // walk unwinds. The cheap per-step chrome (orientation / current source / step chips
  // / bottom nav) stays live so the landing animates and Back/Forward stay responsive.
  // Scope is PER-INSTANCE on purpose: gating on the module-level restoring() would let
  // a user click during a STALE instance's still-open bracket defer-but-never-flush.
  let chromeWalk = 0;
  let journeyDirty = false;
  let exploreDirty = false;
  let lastExplorePlaceKey = ""; // the source/verse the Explore panel last rendered (scroll keep)
  function flushChrome(): void {
    if (journeyDirty) {
      journeyDirty = false;
      refreshJourney();
    }
    if (exploreDirty) {
      exploreDirty = false;
      refreshExplore();
    }
  }

  function setTrail(node: HTMLElement | null): void {
    if (node) clearSelectedVerse(false);
    currentNode = node;
    // Mark the pasuk this chain hangs off (or clear it at depth 0), so reading a
    // source never strands the learner without a visible anchor in the reader.
    setAnchorVerse(node);
    // notifyPath FIRST: the orientation bar below renders the Back/Forward
    // buttons from history state, which must already reflect this move.
    notifyPath(node);
    renderOrientation(node);
    renderCurrentSource(node);
    renderStepChips();
    refreshJourney();
    refreshExplore();
    renderBottomNav();
  }

  // Label the open stack: each open card on the active chain gets a small
  // "N deep" chip in its head - depth from the pasuk, matching the orientation
  // bar's "N steps deep" - so the coloured card edges read as "this source is
  // N deep on your trail", not unexplained vertical lines. Rebuilt on every
  // trail change (cheap: one chip per open row).
  function renderStepChips(): void {
    document.querySelectorAll(".ec-step").forEach((c) => c.remove());
    const { rows } = trailChain(currentNode);
    rows.forEach((r, i) => {
      const head = r.querySelector(":scope > .srowhead");
      if (!(head instanceof HTMLElement)) return;
      const depth = i + 1;
      const chip = el("span", "ec-step", `${depth} deep`);
      chip.title = `${depth} ${depth === 1 ? "source" : "sources"} deep from the pasuk - on your active trail`;
      chip.setAttribute("aria-hidden", "true"); // the head's aria-label + live region already announce depth
      const caret = head.querySelector(":scope > .scaret");
      if (caret) head.insertBefore(chip, caret);
      else head.append(chip);
    });
  }

  // ---- shared "jump back" verbs (orientation crumbs, journey map, explore,
  //      compare modal all reuse these; the chrome is always rebuilt to match
  //      currentNode, so reading the live trailChain is correct) ---------------
  function closeAllDrawers(): void {
    closeJourneyDrawer();
    closeExploreDrawer();
    closeDeskDrawer(false);
    closeBookmarksDrawer(false);
    closeSavedPathsDrawer(false);
  }
  function jumpToAnchor(): void {
    const { verse } = trailChain(currentNode);
    closeAllDrawers();
    if (verse) collapseToVerse(verse);
    else collapseAll();
    announce("Back to the pasuk.");
  }
  // Jump the trail back to a specific open row. Capturing the ROW (not an index)
  // is robust to the trail changing between render and click.
  function jumpToRow(row: HTMLElement): void {
    closeAllDrawers();
    collapseToRow(row);
    announce(`Jumped to ${row.dataset.label ?? "source"}.`);
  }
  function jumpToHop(hopIndex: number): void {
    const { rows } = trailChain(currentNode);
    const row = rows[hopIndex - 1]; // hop 1 == rows[0]
    if (row) jumpToRow(row);
  }
  // "Return to the original source" = collapse all the way back to the pasuk.
  function returnToStart(): void {
    jumpToAnchor();
  }

  // Walk the open DOM path up to the verse, collecting the open source rows.
  function trailChain(node: HTMLElement | null): { verse: HTMLElement | null; rows: HTMLElement[] } {
    const rows: HTMLElement[] = [];
    let verse: HTMLElement | null = null;
    let n: HTMLElement | null = node;
    while (n) {
      if (n.classList.contains("srow") && n.classList.contains("open")) rows.unshift(n);
      if (n.classList.contains("verse")) {
        verse = n;
        break;
      }
      n = n.parentElement;
    }
    return { verse, rows };
  }

  // The ONE relationship rule every trail projection shares (journey list,
  // Current Source header, Explore back-rows, Compare modal). Direction honesty:
  // the FIRST hop is anchored on the VERSE, so its directional label reads true;
  // deeper hops are anchored on a SOURCE, where a non-"on"-shaped ref's direction
  // can invert (this source cites that one, not the reverse) - those read the
  // neutral "cross-reference" rather than assert a backwards citation. EXCEPT a
  // row from the "What this text cites" bucket, which EARNED its label - the dual
  // gate verified the parent's own words cite it - so the stronger, verified
  // "cited here" survives into every projection.
  function trailRel(row: HTMLElement, firstHop: boolean): { label: string; strength: RelStrength } {
    if (row.dataset.bucket === "cited") return { label: "cited here", strength: "related" };
    const rel = getRelationship(row.dataset.linkType ?? "", row.dataset.category ?? "");
    const directional = firstHop || / on /i.test(row.dataset.ref ?? "");
    return directional ? rel : { label: "cross-reference", strength: "thematic" };
  }

  // Build the journey PathNode[] (the projection) from the active DOM chain.
  function activeJourney(): { anchorRef: string; anchorLabel: string; nodes: PathNode[]; rows: HTMLElement[]; verse: HTMLElement | null } {
    const { verse, rows } = trailChain(currentNode);
    const anchorRef = verse?.dataset.ref ?? "";
    const anchorLabel = anchorRef ? verseShort(anchorRef) : rootLabel;
    const nodes: PathNode[] = rows.map((r, i) => {
      const rel = trailRel(r, i === 0);
      return {
        ref: r.dataset.ref ?? "",
        label: r.dataset.label ?? "source",
        labelHe: r.dataset.he || null,
        // relDisplay at the BUILD site so every projection of this node -
        // journey map chips, path-explanation prose, Compare, Summarize -
        // speaks the learner wording ("commentary", never "comments on this").
        relationshipLabel: relDisplay(rel.label),
        strength: rel.strength,
        isVerse: false,
      };
    });
    return { anchorRef, anchorLabel, nodes, rows, verse };
  }

  // ---- orientation bar render ----------------------------------------------
  // The TOP of the GPS: "how far am I from home, and how do I return". A clickable
  // compact path strip (start -> ... -> current), depth + side-path count, and the
  // escape hatches (Back one step, Return to start, Summarize). Hidden at depth 0.
  // Publish the orientation bar's live height as --orient-h so the desktop
  // rails can stick BELOW it (both are in the same sticky stack; without this
  // the bar covers the rail head once the page scrolls). Also registers this
  // bar with the module-level resize re-publisher: the bar wraps/unwraps on
  // window resize without a trail move, and a stale --orient-h would slide the
  // rail back under it (code-review fix).
  function publishOrientHeight(): void {
    liveOrientBar = orientationBar;
    wireOrientResize();
    publishOrientHeightFor(orientationBar);
  }

  // The "Your trail" rail collapse toggle (desktop-only via CSS). Always lives in the
  // orientation bar - which we keep present (minimal) even at depth 0 - so the toggle
  // is ALWAYS findable. Reads/writes the shell's data-rail-open + persists.
  function railToggleEl(): HTMLButtonElement {
    const shell = document.querySelector<HTMLElement>(".shell");
    const railOpen = shell?.dataset.railOpen !== "false";
    const railToggle = el("button", "rail-toggle orient-rail-toggle") as HTMLButtonElement;
    railToggle.type = "button";
    // VISIBLE label (desktop) so the control is self-evident without hovering. "trail"
    // mirrors the panel's "Your trail" heading (consistency); on screen + adjacent to
    // the panel it reads as the panel, not the study trail. The visible text IS the
    // accessible name (icon is aria-hidden); aria-expanded carries open/collapsed.
    railToggle.setAttribute("aria-expanded", String(railOpen));
    const icon = el("span", "hi hi-sidebar");
    icon.setAttribute("aria-hidden", "true");
    const lbl = el("span", "rail-toggle-txt", railOpen ? "Hide trail" : "Show trail");
    railToggle.append(icon, lbl);
    railToggle.addEventListener("click", () => {
      if (!shell) return;
      const nowOpen = shell.dataset.railOpen !== "false";
      shell.dataset.railOpen = nowOpen ? "false" : "true";
      railToggle.setAttribute("aria-expanded", String(!nowOpen));
      lbl.textContent = nowOpen ? "Show trail" : "Hide trail";
      void persistRailOpen(!nowOpen);
    });
    return railToggle;
  }

  function renderOrientation(node: HTMLElement | null): void {
    const { rows } = trailChain(node);
    if (!rows.length) {
      // Depth 0: a MINIMAL bar carrying only the rail toggle, so it never vanishes.
      lastOrientDepth = 0;
      orientationBar.classList.add("orientbar-min");
      orientationBar.replaceChildren(railToggleEl());
      orientationBar.hidden = false;
      publishOrientHeight();
      return;
    }
    orientationBar.classList.remove("orientbar-min");
    orientationBar.hidden = false;
    const { anchorLabel, nodes } = activeJourney();
    const depth = rows.length;
    const grew = depth > lastOrientDepth;
    lastOrientDepth = depth;

    const pin = el("span", "orient-pin");
    pin.setAttribute("aria-hidden", "true");

    // the clickable path strip (truncates the middle, keeps start + current)
    const strip = el("div", "orient-path");
    strip.setAttribute("aria-label", "Trail from your starting source to here");
    const crumbs = compactPath(anchorLabel, nodes, isDesktop() ? 5 : 3);
    crumbs.forEach((c, i) => {
      if (i > 0) {
        const sep = el("span", "orient-sep", " -> ");
        sep.setAttribute("aria-hidden", "true");
        strip.append(sep);
      }
      if (c.kind === "gap") {
        strip.append(el("span", "orient-gap", "..."));
        return;
      }
      if (c.isCurrent) {
        const cur = el("span", "orient-crumb orient-crumb-cur", c.label);
        cur.setAttribute("aria-current", "true");
        if (grew) cur.classList.add("orient-crumb-new"); // brief acknowledge pulse
        strip.append(cur);
        return;
      }
      const crumb = el("button", "orient-crumb");
      crumb.type = "button";
      crumb.textContent = c.label;
      crumb.setAttribute("aria-label", `Jump back to ${c.label}`);
      crumb.addEventListener("click", () => (c.kind === "anchor" ? jumpToAnchor() : jumpToHop(c.index)));
      strip.append(crumb);
    });

    const meta = el("span", "orient-meta");
    const sides = sidePaths.length;
    meta.textContent =
      `${depth} ${depth === 1 ? "step" : "steps"} deep` +
      (sides ? ` · ${sides} side path${sides === 1 ? "" : "s"}` : "");

    const acts = el("div", "orient-acts");
    const back = el("button", "orient-back");
    back.type = "button";
    back.setAttribute("aria-label", "Back - return to where you just were");
    back.append(el("span", "orient-back-i"), document.createTextNode("Back"));
    // v7: Back is HISTORY back (browser semantics - restores the previous
    // state, including a branch the accordion auto-closed). The structural
    // fallback covers a cold deep-linked visitor whose entry 0 IS the deep
    // chain - their first Back must never exit the app.
    back.addEventListener("click", () => {
      if (navHistory.canGoBackInApp()) {
        history.back();
      } else {
        // Structural fallback (cold deep-linked visitor standing on entry 0):
        // REWRITE this entry rather than pushing, or the deep route would sit
        // one entry below and the next Back would re-open it (ping-pong).
        replaceNextTrailWrite = true;
        backOne();
        replaceNextTrailWrite = false; // backOne may no-op at depth 0 - never leak
      }
    });

    // Forward exists only after going back (browser semantics); disabled
    // otherwise. Enablement re-renders on every setTrail/traversal.
    const fwd = el("button", "orient-back orient-fwd");
    fwd.type = "button";
    fwd.setAttribute("aria-label", "Forward - return to where you went back from");
    fwd.append(document.createTextNode("Forward"), el("span", "orient-back-i orient-fwd-i"));
    fwd.disabled = !navHistory.canGoForward();
    fwd.setAttribute("aria-disabled", String(fwd.disabled));
    fwd.addEventListener("click", () => {
      if (navHistory.canGoForward()) history.forward();
    });

    const ret = el("button", "orient-return", "Return to start");
    ret.type = "button";
    ret.setAttribute("aria-label", "Return to start - original source");
    ret.addEventListener("click", returnToStart);

    const sum = el("button", "orient-sum orient-wide-only", "Your trail");
    sum.type = "button";
    sum.setAttribute("aria-haspopup", "dialog");
    sum.setAttribute("aria-label", "Open your trail compass");
    sum.addEventListener("click", openJourneyDrawer);

    // "Explore" entry for the mid-width range (right rail collapsed, no bottom nav).
    const expl = el("button", "orient-sum orient-explore", "Explore");
    expl.type = "button";
    expl.setAttribute("aria-haspopup", "dialog");
    expl.setAttribute("aria-label", "Open where you can go next");
    expl.addEventListener("click", openExploreDrawer);

    const bookmarks = el("button", "orient-sum orient-bookmarks", bookmarkCount ? `Bookmarks (${bookmarkCount})` : "Bookmarks");
    bookmarks.type = "button";
    bookmarks.setAttribute("aria-haspopup", "dialog");
    bookmarks.setAttribute("aria-label", "Open Bookmarks");
    bookmarks.addEventListener("click", openBookmarksSurface);

    const savedPaths = el("button", "orient-sum orient-paths", "Saved Trails");
    savedPaths.type = "button";
    savedPaths.setAttribute("aria-haspopup", "dialog");
    savedPaths.setAttribute("aria-label", "Open Saved Trails");
    savedPaths.addEventListener("click", openSavedPathsSurface);

    acts.append(expl, back, fwd, ret, sum, bookmarks, savedPaths);
    // The rail toggle lives WITH the breadcrumb (left of it).
    orientationBar.replaceChildren(railToggleEl(), pin, strip, meta, acts);
    publishOrientHeight();
  }
  renderOrientation(null);

  // ---- Current Source identity card render ---------------------------------
  // Projection of the deepest open source: title (En + He), where it opened from,
  // the honest relationship, depth, and escape hatches. Hidden at depth 0 (the
  // parasha header already names what you are reading then).
  function renderCurrentSource(node: HTMLElement | null): void {
    const { rows, verse } = trailChain(node);
    if (!rows.length) {
      currentSourceCard.replaceChildren();
      currentSourceCard.hidden = true;
      return;
    }
    currentSourceCard.hidden = false;
    const cur = rows[rows.length - 1];
    const parent = rows.length >= 2 ? rows[rows.length - 2] : null;
    const fromLabel = parent ? parent.dataset.label ?? "source" : verse ? verseShort(verse.dataset.ref ?? "") : "the pasuk";
    const type = getSourceType(cur.dataset.category ?? "");
    const depth = rows.length;
    // The shared projection rule (trailRel): direction honesty + earned "cited here".
    const relLabel = trailRel(cur, depth === 1).label;

    const eyebrow = el("div", "csource-eyebrow", "Current source");

    const titleRow = el("div", "csource-titlerow");
    const dot = el("span", "ec-dot csource-dot");
    dot.style.setProperty("--dot", `var(${type.colorVar})`);
    dot.setAttribute("aria-hidden", "true");
    const titles = el("div", "csource-titles");
    if (cur.dataset.he) {
      const he = el("span", "csource-he", cur.dataset.he);
      he.lang = "he";
      he.dir = "rtl";
      titles.append(he);
    }
    titles.append(el("span", "csource-en", cur.dataset.label ?? "source"));
    titleRow.append(dot, titles);

    const meta = el("div", "csource-meta");
    const from = el("span", "csource-from");
    from.append(el("span", "csource-mut", "Opened from "), document.createTextNode(fromLabel));
    meta.append(from, relChip(relDisplay(relLabel)), el("span", "csource-depth", `${depth} ${depth === 1 ? "step" : "steps"} from start`));
    // A subtle era/year next to the source identity - ONLY when we actually know it.
    const se = subtleEra(stepEra(cur));
    if (se) meta.append(el("span", "csource-era", se.year ? `${se.label} · ${se.year}` : se.label));

    // The header stays MINIMAL on purpose (v5 simplification): identity + where
    // you came from + one local action. The old view tabs are gone - their
    // default ("Text") state IS the new collapsed-bucket default, and each
    // bucket's disclosure line carries its own count. "Compare to start" lives
    // in the journey drawer; "Return to start" stays in the orientation bar
    // (always visible whenever this header is).
    const acts = el("div", "csource-acts");
    const back = button("csource-btn", parent ? `Back to ${truncLabel(fromLabel)}` : "Back to the pasuk", backOne);
    back.setAttribute("aria-label", parent ? `Back to ${truncLabel(fromLabel)}` : "Back to the pasuk");
    acts.append(back);

    currentSourceCard.replaceChildren(eyebrow, titleRow, meta, acts);
  }
  renderCurrentSource(null);

  // Collapse the single deepest open row in the active chain (back one step).
  function backOne(): void {
    const { rows } = trailChain(currentNode);
    const deepest = rows[rows.length - 1];
    if (deepest) {
      // close the deepest row + its open descendants (single trail write)
      closeRowTree(deepest);
      const parentRow = parentSrow(deepest);
      setTrail(parentRow);
      announce(parentRow ? `Back to ${parentRow.dataset.label}.` : "Back to the pasuk.");
    }
  }

  function parentSrow(row: HTMLElement): HTMLElement | null {
    let n: HTMLElement | null = row.parentElement;
    while (n) {
      if (n.classList.contains("srow") && n.classList.contains("open")) return n;
      if (n.classList.contains("verse")) return null;
      n = n.parentElement;
    }
    return null;
  }

  // ---- collapse / return routes (unchanged mechanism) ----------------------
  function collapseWithin(scope: ParentNode): void {
    scope.querySelectorAll(".srow.open").forEach((r) => {
      r.classList.remove("open");
      r.querySelector(":scope > .srowhead")?.setAttribute("aria-expanded", "false");
    });
  }

  // Close ONE open row and everything open inside it - the shared close
  // primitive (backOne, the sibling accordion, route restores), so the
  // open-class + aria-expanded pair can never drift apart.
  function closeRowTree(row: HTMLElement): void {
    collapseWithin(row);
    row.classList.remove("open");
    row.querySelector(":scope > .srowhead")?.setAttribute("aria-expanded", "false");
  }

  // ACCORDION invariant support (v7): opening a row under verse B while a
  // chain is open under verse A closes A's chain. The PANEL stays open -
  // panels are browse state, not chain state - but open .srow rows app-wide
  // must equal exactly the one chain the route hash encodes, or a history
  // restore is lossy. ONE document scan for open rows (the invariant keeps
  // them to a single chain) - never a per-verse subtree sweep on every tap.
  function closeOtherVerseChains(row: HTMLElement): void {
    const ownVerse = row.closest(".verse");
    const closedVerses = new Set<Element>();
    for (const open of document.querySelectorAll(".srow.open")) {
      if (open === row || (ownVerse && ownVerse.contains(open))) continue;
      const v = open.closest(".verse");
      if (v instanceof HTMLElement && !closedVerses.has(v)) {
        closedVerses.add(v);
        collapseWithin(v);
        // closed rows become language-filterable again - keep counts honest
        v.querySelectorAll(".sources, .citelist").forEach((l) => {
          if (l instanceof HTMLElement) annotateLang(l);
        });
      }
    }
  }

  function collapseToRow(row: HTMLElement): void {
    collapseWithin(row);
    setTrail(row);
  }

  function collapseToVerse(verse: HTMLElement): void {
    collapseWithin(verse);
    verse.classList.remove("show-sources");
    verse.querySelector(":scope > .vactions > .srcbtn")?.setAttribute("aria-expanded", "false");
    setTrail(null);
  }

  function collapseAll(): void {
    document.querySelectorAll(".show-sources").forEach((n) => n.classList.remove("show-sources"));
    document.querySelectorAll(".srow.open").forEach((r) => r.classList.remove("open"));
    document.querySelectorAll('[aria-expanded="true"]').forEach((h) => h.setAttribute("aria-expanded", "false"));
    setTrail(null);
    announce("Back to the pasuk.");
    const verses = document.querySelector(".verses");
    if (verses instanceof HTMLElement) {
      verses.tabIndex = -1;
      verses.focus();
    }
  }

  // ---- verse-level source panel --------------------------------------------
  // ---- per-verse source scent (Tranche 3): chapter-batched, idle-time,
  //      enhancement-only. NEVER per-verse request fan-out. ------------------
  // verseScent holds the ACCEPTED links per verse (reduced from the chapter
  // response, which is discarded - a Genesis 1 chapter is ~11k links and must
  // not be retained). Tiers/families recompute from it on language-mode change.
  const verseScent = new Map<string, SourceLink[]>();
  const scentBtns = new Map<string, HTMLButtonElement>();

  function paintScent(ref: string): void {
    const btn = scentBtns.get(ref);
    if (!btn) return;
    btn.querySelector(":scope > .scent")?.remove();
    const lbl = btn.querySelector<HTMLElement>(":scope > .srcbtn-l");
    const links = verseScent.get(ref);
    const visible = links?.length ? scentVisible(links, getSourceLangMode()) : [];
    const total = visible.length;
    if (total === 0) {
      // zero accepted/visible sources -> the chip stays absent (never "0 sources")
      btn.classList.add("srcbtn-empty");
      if (lbl) lbl.textContent = "";
      btn.removeAttribute("title");
      return;
    }
    // A single, subtle TOTAL count - the per-family colored breakdown moved to the
    // hover title + the wide Explore panel ("What connects here"), keeping the
    // reading center calm. The visible digit is the exact accepted-source count.
    btn.classList.remove("srcbtn-empty");
    const breakdown = scentBreakdown(scentFamilies(visible));
    if (lbl) lbl.textContent = `${total} ${total === 1 ? "source" : "sources"}`;
    btn.title = `Sources here: ${breakdown}`;
    // spelled-out text for screen readers (the chip digit is terse)
    btn.append(el("span", "scent visually-hidden", ` - ${total} ${total === 1 ? "source" : "sources"}: ${breakdown}`));
  }

  let terrainStarted = false;
  function startTerrain(): void {
    if (terrainStarted) return;
    terrainStarted = true;
    // Under vitest, auto-start is suppressed (the many unrelated navigator
    // mounts must not fire stray idle fetches after their tests end); the
    // scent dom test opts back in via _enableTerrainForTests().
    if (import.meta.env.MODE === "test" && !TERRAIN_IN_TESTS) return;
    // scent is a nicety: skip entirely on data-saver connections
    const nav = globalThis.navigator as Navigator & { connection?: { saveData?: boolean } };
    if (nav?.connection?.saveData) return;
    const idle = (cb: () => void): void => onIdle(cb, SCENT_IDLE_FALLBACK_MS);
    idle(() => void runTerrain());
  }

  async function runTerrain(): Promise<void> {
    let book = "";
    let startCh = 0;
    let endCh = 0;
    try {
      ({ book, startCh, endCh } = parseParashaRef(parashaRef));
    } catch {
      return;
    }
    // DOM sentinel: when the reader re-renders (route change), stop silently.
    const sentinel: HTMLElement | undefined = scentBtns.values().next().value;
    if (!sentinel) return;
    // nearest-first: start from the chapter the learner is anchored in
    const chapters: number[] = [];
    for (let c = startCh; c <= endCh; c++) chapters.push(c);
    const { verse } = trailChain(currentNode);
    const m = (verse?.dataset.ref ?? "").match(/ (\d+):\d+$/);
    const first = m ? +m[1] : startCh;
    chapters.sort((a, b) => Math.abs(a - first) - Math.abs(b - first));
    for (const ch of chapters) {
      if (!sentinel.isConnected) return;
      let links: SourceLink[];
      try {
        links = await getChapterLinks(`${book} ${ch}`); // sequential: one at a time
      } catch {
        terrainFailed = true;
        if (selectedVerseRef) refreshExplore();
        return; // silent + non-blocking: the buttons simply stay undecorated
      }
      const dist = distributeChapterLinks(links);
      const prefix = `${book} ${ch}:`;
      for (const [vref, ls] of dist) {
        // only THIS chapter's verses: a cross-chapter range link arrives again
        // with its own chapter's fetch - taking it here would double-count it.
        if (!vref.startsWith(prefix)) continue;
        // mirror the panel's dedupe (one row per commentator verse-ref, English
        // availability merged across segments) so the scent counts exactly what
        // the opened panel will show - Rashi's 13:2:1 + 13:2:2 is ONE source.
        const byKey = new Map<string, SourceLink>();
        for (const l of ls) {
          const key = commentatorVerseRef(l);
          const prior = byKey.get(key);
          if (!prior) {
            byKey.set(key, l);
            continue;
          }
          byKey.set(key, { ...prior, hasEn: mergeAvail(prior.hasEn, l.hasEn) });
        }
        verseScent.set(vref, [...byKey.values()]);
        paintScent(vref);
      }
      // ONE panel repaint per chapter: the selected verse (if it lives here) sees its
      // freshly-set scent AND the chapter's loaded flag in the same rebuild.
      scentChaptersLoaded.add(ch);
      if (selectedVerseRef && verseChapter(selectedVerseRef) === ch) refreshExplore();
    }
  }

  function attachVerse(verseEl: HTMLElement, ref: string): void {
    const panel = el("div", "sources");
    const panelId = nextId("src");
    panel.id = panelId;
    // A subtle count chip (the scent total) - NOT the old verbose "Explore sources"
    // expander. It still opens the engine (replayPath clicks it) but the sources now
    // read in the wide Explore panel, so the center stays calm. Empty until scent paints.
    const btn = el("button", "srcbtn srcbtn-empty");
    btn.type = "button";
    const lbl = el("span", "srcbtn-l", "");
    btn.append(lbl);
    btn.setAttribute("aria-expanded", "false");
    btn.setAttribute("aria-controls", panelId);
    btn.addEventListener("click", () => {
      const open = verseEl.classList.toggle("show-sources");
      btn.setAttribute("aria-expanded", String(open));
      if (!open && verseEl.querySelector(".srow.open")) {
        // Closing the panel with an open chain inside: close the chain too,
        // so the route hash never encodes rows that are no longer visible
        // (the pre-v7 "ghost chain" divergence).
        const hadCurrent = currentNode !== null && verseEl.contains(currentNode);
        collapseWithin(verseEl);
        if (hadCurrent) setTrail(null);
      }
      if (open) {
        if (!panel.dataset.loaded) {
          panel.dataset.loaded = "1";
          void loadSources(panel, ref, 1, lbl);
        }
        reveal(panel);
        // footprint: this pasuk's sources were opened (idempotent, fire-and-
        // forget). Restore walks open panels too - those are navigation, not
        // exploration, and must not inflate the persisted footprints.
        if (!restoring()) {
          void markVerseVisited(ref).catch(() => {});
          addFootprint(verseEl);
          // surface this verse's sources in the wide Explore panel (the center
          // accordion stays hidden); on narrow viewports open the Explore drawer.
          selectVerse(verseEl, ref);
          if (!isDesktop()) openExploreDrawer();
        }
      }
    });
    const actions = el("div", "vactions");
    actions.append(btn);
    verseEl.append(actions, panel);
    const clickSelect = (e: Event): void => {
      const sel = globalThis.getSelection?.();
      if (sel && !sel.isCollapsed) return;
      if ((e.target instanceof Element && e.target.closest("a, button")) || !verseEl.isConnected) return;
      selectVerse(verseEl, ref);
    };
    const selectTargets = [...verseEl.querySelectorAll<HTMLElement>(":scope > .vhe, :scope > .ven")];
    const primaryTarget = selectTargets[0] ?? null;
    for (const target of selectTargets) {
      target.classList.add("verse-select-target");
      target.addEventListener("click", clickSelect);
    }
    if (primaryTarget) {
      primaryTarget.classList.add("verse-select-target-primary");
      primaryTarget.tabIndex = 0;
      primaryTarget.setAttribute("role", "button");
      primaryTarget.setAttribute("aria-pressed", "false");
      primaryTarget.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        selectVerse(verseEl, ref);
      });
    }
    // returning learner: surface last sessions' footprint once the set loads
    void verseVisitedReady.then((set) => {
      if (set.has(ref)) addFootprint(verseEl);
    });
    // scent: register the button; paint immediately if the chapter already landed
    scentBtns.set(ref, btn);
    if (verseScent.has(ref)) paintScent(ref);
  }

  function openVerseSourcesFromExplore(verseEl: HTMLElement, ref: string): void {
    const btn = verseEl.querySelector(":scope > .vactions > .srcbtn");
    const panel = verseEl.querySelector(":scope > .sources");
    if (!(btn instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;
    // Surface the verse's (hidden) source engine DIRECTLY. We must NOT btn.click():
    // the srcbtn handler also calls openExploreDrawer() on mobile, which - since we
    // are already INSIDE the Explore drawer (this runs from the drawer's "Open") -
    // would TOGGLE the open drawer shut. That was the "open a source -> it doesn't
    // open, goes back to the verses" bug. Replicate just the open + footprint here.
    if (!verseEl.classList.contains("show-sources")) {
      verseEl.classList.add("show-sources");
      btn.setAttribute("aria-expanded", "true");
      if (!restoring()) {
        void markVerseVisited(ref).catch(() => {});
        addFootprint(verseEl);
      }
    }
    if (!panel.dataset.loaded) {
      panel.dataset.loaded = "1";
      const label = btn.querySelector(":scope > .srcbtn-l");
      void loadSources(panel, ref, 1, label instanceof HTMLElement ? label : undefined);
    }
    reveal(panel);
  }

  async function continueByVerseRef(ref: string): Promise<boolean> {
    if (!(selectedVerseEl instanceof HTMLElement) || !selectedVerseRef) return false;
    openVerseSourcesFromExplore(selectedVerseEl, selectedVerseRef);
    const panel = selectedVerseEl.querySelector(":scope > .sources");
    if (!(panel instanceof HTMLElement)) return false;
    await waitFor(() => !!panel.querySelector(`.srow[data-ref="${attrEsc(ref)}"]`), 8000);
    const row = panel.querySelector(`.srow[data-ref="${attrEsc(ref)}"]`);
    if (!(row instanceof HTMLElement)) return false;
    revealRow(row);
    const head = row.querySelector(":scope > .srowhead");
    // Keep the Explore drawer OPEN (mobile) so the just-opened source shows in its
    // reader, instead of dropping the learner back to the verse list ("it didn't
    // open"). The open handler (toggleRow -> setTrail -> refreshExplore) repaints the
    // drawer; an already-open row gets no click, so refresh explicitly. (Desktop: no-op.)
    if (!row.classList.contains("open") && head instanceof HTMLElement) head.click();
    else refreshExplore();
    return true;
  }

  // A verse source card: ONE action, "Open", that commits the source into the path
  // and reads it in the panel (the old "Read here" peek + "Open in path" duality is
  // gone - one reading model). Bookmark + Add to Desk stay.
  function buildVerseReaderCard(item: Item, anchorRef: string, disambig?: string): HTMLElement {
    const rel = getRelationship(item.linkType, item.category);
    const conn = toConn(item, reasonText(rel.label, anchorRef), undefined, undefined, anchorRef);
    // Every card hangs off the SAME pasuk (named once in the panel head), so the
    // structural "Commentary on 16:1" line is pure repetition - show a real
    // description of the work, or nothing. When several passages of the same work
    // appear in one section, append the disambiguator (the unique ref tail) so the
    // cards both carry the work's description AND read apart: "<desc> (<passage>)".
    const desc = describeSource(item.commentator, item.ref);
    conn.contextLine = disambig ? (desc ? `${desc} (${disambig})` : disambig) : desc;
    const acts = {
      onContinue: (): void => void withTrailSurface("right-rail", () => continueByVerseRef(conn.id)),
      onAddToDesk: (c: SourceConnection, btn: HTMLButtonElement): void => addDeskIdentity(connToDeskItem(c), btn),
      onBookmark: (c: SourceConnection, btn: HTMLButtonElement): void => void bookmarkIdentity(toBookmarkItem(c), btn),
    };
    return renderSourceCard(conn, acts, { debug: false });
  }

  function buildVerseReaderGroup(items: Item[], anchorRef: string): { nodes: HTMLElement[]; shown: number } {
    if (!items.length) return { nodes: [], shown: 0 };
    // Combine TRUE duplicates: the identical ref appearing twice (a Sefaria data
    // quirk) collapses to a single card. Distinct passages of the same work keep
    // their own card and are told apart by disambiguateSameWork below.
    const seen = new Set<string>();
    const unique = items.filter((it) => (seen.has(it.ref) ? false : (seen.add(it.ref), true)));
    const wrap = el("div", "explore-cards");
    const disambig = disambiguateSameWork(unique);
    for (const item of unique) wrap.append(buildVerseReaderCard(item, anchorRef, disambig.get(item)));
    return { nodes: [wrap], shown: unique.length };
  }

  function buildVerseExploreSections(anchorRef: string, links: SourceLink[]): HTMLElement[] {
    const out: HTMLElement[] = [];
    // Every category starts COLLAPSED so a long first list never hides the ones below.
    for (const sec of sectionizeGroups(groupLinks(links))) {
      const group = buildVerseReaderGroup(sec.items, anchorRef);
      if (!group.shown) continue;
      out.push(
        ...exploreAccordion(sec.label, sec.key, group, {
          defaultOpen: false,
          memoryKey: `verse:${anchorRef}::${sec.key}`,
          dotVar: sec.colorVar,
        }),
      );
    }
    return out;
  }

  async function loadSources(panel: HTMLElement, ref: string, depth: number, countLabel?: HTMLElement): Promise<void> {
    panel.replaceChildren(loadingRow());
    try {
      const links = await getLinks(ref);
      const groups = groupLinks(links);
      const total = groups.reduce((s, g) => s + g.items.length, 0);
      if (countLabel) {
        countLabel.textContent = total ? `${total} ${total === 1 ? "source" : "sources"}` : "";
        countLabel.closest(".srcbtn")?.classList.toggle("srcbtn-empty", total === 0);
      }
      const rows = sourceRows(groups, depth, ref);
      panel.replaceChildren(...(rows.length ? rows : [note("No sources in the mesorah for this pasuk yet.")]));
      if (rows.length) annotateLang(panel); // born filtered when a mode is already active
    } catch (err) {
      panel.replaceChildren(
        errorRow(err, () => {
          panel.dataset.loaded = "1";
          void loadSources(panel, ref, depth, countLabel);
        }),
      );
    }
  }

  // The verse-level source list, as labelled SECTIONS. The broad "Commentary"
  // category is split by torah-map's curated era tiers (classic / later / more -
  // see era.ts). Every other category presents under its honest TYPE heading,
  // and sibling categories that SHARE a type label (Kabbalah / Chasidut / Musar /
  // Jewish Thought all read "Jewish Thought"; Mishnah / Talmud / Tosefta read
  // "Talmud") merge into ONE section - never the same heading several times in a
  // row. Long sections fold behind "Show more" so the list never becomes a wall.
  function sourceRows(groups: Array<{ category: string; items: Item[] }>, depth: number, anchorRef: string): HTMLElement[] {
    const out: HTMLElement[] = [];
    sectionizeGroups(groups).forEach((s, i) => {
      // Commentary sections name their era in the per-card reason line ("Classic
      // commentary on <anchor>"); every other section uses foldedSection's default.
      const reason = s.commentaryKey
        ? s.commentaryKey === "classic"
          ? `Classic commentary on ${anchorRef}`
          : s.commentaryKey === "later"
            ? `Later commentary on ${anchorRef}`
            : `Commentary on ${anchorRef}`
        : undefined;
      // Accordion default: the FIRST section (the most relevant - classic
      // commentaries when present) opens, the rest start collapsed so a long list
      // never dominates the page. Session memory (a reader's own toggles) wins on re-render.
      out.push(...foldedSection(s.label, s.colorVar, s.items, depth, { anchorName: anchorRef, reasonOverride: reason }, i === 0));
    });
    return out;
  }

  // How many visual units (cards / bundles) a section shows before folding the
  // rest behind "Show more". Folding only kicks in past +1 (never "Show 1 more").
  const SECTION_VISIBLE = 6;

  // One labelled ACCORDION section: heading button + its card clusters, folding
  // units beyond SECTION_VISIBLE. Folded units are hidden DIRECT siblings tagged
  // data-more (exactly the bundle pattern), so the replayPath `:scope > .srow`
  // contract is untouched and a deep-link can still land on a folded card
  // (expandMore runs before the replay click). "Show more" expansion is one-way;
  // the SECTION collapse is the reversible layer on top, carried by a separate
  // data-secfold attribute (CSS-hidden) so it never fights `hidden`'s owners.
  // Every member (cluster roots, bundle members, the more-button) is stamped
  // data-sec so toggleRow/replayPath/continueByRef can force the section open
  // before revealing a target row.
  function foldedSection(
    label: string,
    colorVar: string,
    items: Item[],
    depth: number,
    opts: CardOpts,
    defaultOpen = true,
  ): HTMLElement[] {
    const clusters = cardClusters(items, depth, opts);
    const secId = nextId("sec");
    const memKey = `${opts.anchorName ?? ""}::${label}`;
    const open = sectionMemory.get(memKey) ?? defaultOpen;
    const out: HTMLElement[] = [sectionHead(label, items.length, colorVar, secId, memKey, open)];
    const members: HTMLElement[] = [];
    if (clusters.length > SECTION_VISIBLE + 1) {
      const moreId = nextId("more");
      const hidden = clusters.slice(SECTION_VISIBLE);
      for (const cluster of hidden) {
        // Only the cluster ROOT (single card or bundle header) is fold-governed;
        // bundle members stay governed by their own bundle fold.
        cluster[0].hidden = true;
        cluster[0].dataset.more = moreId;
      }
      members.push(...clusters.flat());
      const btn = el("button", "ec-more", `Show ${hidden.length} more in this section`);
      btn.type = "button";
      btn.dataset.moreBtn = moreId;
      btn.dataset.secLabel = label; // annotateLang retitles against this when a mode hides tail rows
      btn.setAttribute("aria-label", `Show ${hidden.length} more under ${label}`);
      btn.addEventListener("click", () => expandMore(moreId));
      members.push(btn);
    } else {
      members.push(...clusters.flat());
    }
    for (const m of members) {
      m.dataset.sec = secId;
      if (!open) m.setAttribute("data-secfold", "");
    }
    out.push(...members);
    return out;
  }

  // Reveal a folded section tail (one-way). Also used by deep-link replay and
  // the Explore panel's Continue, so a fold can never strand a target card.
  function expandMore(moreId: string): void {
    document.querySelectorAll(`[data-more="${attrEsc(moreId)}"]`).forEach((m) => {
      if (m instanceof HTMLElement) {
        m.hidden = false;
        delete m.dataset.more;
      }
    });
    document.querySelector(`.ec-more[data-more-btn="${attrEsc(moreId)}"]`)?.remove();
  }

  // Per-card render options, threaded through sections and buckets. anchorName is
  // the REAL thing this list hangs off (the verse ref, or the parent source's
  // label) - it powers the honest per-card reason line; reasonOverride wins when
  // the bucket already knows the more precise claim ("Cited in ...").
  interface CardOpts {
    relOverride?: string;
    anchorName?: string;
    reasonOverride?: string;
  }

  // Render a list of same-context items into CLUSTERS of sibling elements - one
  // cluster per visual unit: a single card, or a bundle (header + its member
  // cards) when ONE commentator appears many times (Rashi a dozen times on a
  // daf). Members stay DIRECT siblings (a `data-bundle` tag, `hidden` until
  // expanded) so the replayPath `:scope > .srow` contract and the DOM-path trail
  // keep working unchanged - bundle and section folds are purely visual.
  function cardClusters(items: Item[], depth: number, opts: CardOpts = {}): HTMLElement[][] {
    const out: HTMLElement[][] = [];
    for (const g of bundleByCommentator(items)) {
      if (g.items.length < 2) {
        out.push([sourceCard(g.items[0], depth, opts)]);
        continue;
      }
      const bundleId = nextId("bundle");
      const cluster: HTMLElement[] = [bundleHeader(g, bundleId, depth, opts.relOverride)];
      g.items.forEach((it) => {
        const card = sourceCard(it, depth, opts);
        card.classList.add("ec-bundled");
        card.dataset.bundle = bundleId;
        card.hidden = true;
        cluster.push(card);
      });
      out.push(cluster);
    }
    return out;
  }

  // Flat form of cardClusters, for the rabbit-hole buckets (no section fold).
  function cardSequence(items: Item[], depth: number, opts: CardOpts = {}): HTMLElement[] {
    return cardClusters(items, depth, opts).flat();
  }

  // The stand-in row for N comments by one commentator. It is NOT a `.srow` (it
  // carries no data-ref and never joins the trail) - tapping it only reveals or
  // hides its member cards. Mirrors sourceCard's direction-honest label so a
  // deep "cross-reference" bundle never asserts a backwards citation.
  function bundleHeader(
    g: { commentator: string; commentatorHe: string | null; items: Item[] },
    bundleId: string,
    depth: number,
    relOverride?: string,
  ): HTMLElement {
    const sample = g.items[0];
    const type = getSourceType(sample.category);
    const rel = getRelationship(sample.linkType, sample.category);
    const onShaped = / on /i.test(sample.ref);
    const relText = relOverride ?? (depth > 1 && !onShaped ? "cross-reference" : rel.label);
    const count = g.items.length;

    const head = el("button", "ec-bundle");
    head.type = "button";
    head.dataset.bundleHead = bundleId;
    // The bundle is one commentator, so the group's availability is the merge of
    // its members' (any-true wins; all-false is false; else unknown).
    const merge = (vals: Array<boolean | null>): boolean | null =>
      vals.includes(true) ? true : vals.every((v) => v === false) ? false : null;
    const bEn = merge(g.items.map((it) => hasEnglishAvailable(it)));
    const bHe = merge(g.items.map((it) => hasHebrewAvailable(it)));
    if (bEn !== null) head.dataset.hasen = bEn ? "1" : "0";
    if (bHe !== null) head.dataset.hashe = bHe ? "1" : "0";
    head.style.setProperty("--tc", `var(${type.colorVar})`);
    head.setAttribute("aria-expanded", "false");
    const hePrefix = g.commentatorHe ? `${g.commentatorHe}, ` : "";
    head.setAttribute("aria-label", `${hePrefix}${g.commentator}, ${count} comments. Show them.`);

    const dot = el("span", "ec-dot");
    dot.style.setProperty("--dot", `var(${type.colorVar})`);
    dot.setAttribute("aria-hidden", "true");

    const cardBody = el("span", "ec-body");
    if (g.commentatorHe) {
      const he = el("span", "ec-he", g.commentatorHe);
      he.lang = "he";
      he.dir = "rtl";
      cardBody.append(he);
    }
    cardBody.append(el("span", "ec-en", g.commentator));
    // Type + relationship are inspection vocabulary on a card - the section
    // heading already says what these are. Debug-only (see sourceCard).
    if (isSourceDebug()) {
      const metaRow = el("span", "ec-meta");
      metaRow.append(document.createTextNode(type.label), relChip(relText, "rel-chip-card"));
      cardBody.append(metaRow);
    }

    const n = el("span", "ec-bundle-n", String(count));
    n.setAttribute("aria-hidden", "true");

    head.append(dot, cardBody, n, el("span", "scaret ec-caret"));
    head.addEventListener("click", () => setBundleExpanded(bundleId, !head.classList.contains("open")));
    return head;
  }

  // Reveal / hide a bundle's member cards. Collapsing never hides a member that is
  // open (or holds an open descendant), so the active trail stays on screen even
  // when the reader folds the bundle back up.
  function setBundleExpanded(bundleId: string, open: boolean): void {
    const head = document.querySelector(`.ec-bundle[data-bundle-head="${attrEsc(bundleId)}"]`);
    if (head instanceof HTMLElement) {
      // A bundle folded inside a section tail must surface its whole section
      // first, so an expanded bundle is never headless under a hidden fold.
      if (open && head.dataset.more) expandMore(head.dataset.more);
      head.classList.toggle("open", open);
      head.setAttribute("aria-expanded", String(open));
    }
    document.querySelectorAll(`.srow.ec-bundled[data-bundle="${attrEsc(bundleId)}"]`).forEach((m) => {
      const row = m as HTMLElement;
      row.hidden = open ? false : !(row.classList.contains("open") || !!row.querySelector(".srow.open"));
    });
  }

  // The shared ACCORDION-HEAD core: the data-acc/data-seckey/data-total wiring,
  // open state, click handler, and the count (`.ec-group-n` - annotateLang's
  // "8 of 12" retitle hook) + caret spans that BOTH disclosure families (verse
  // sections, rabbit-hole buckets) must agree on. The accordion CONTRACT is the
  // data-acc ATTRIBUTE - setSectionExpanded and annotateLang select on it, so
  // a styling-class rename can never silently detach a head from the channel.
  function accHead(cls: string, labelNodes: Node[], count: number, secId: string, memKey: string, open: boolean): HTMLButtonElement {
    const head = el("button", cls);
    head.type = "button";
    head.dataset.acc = secId;
    head.dataset.seckey = memKey;
    head.dataset.total = String(count);
    head.classList.toggle("open", open);
    head.setAttribute("aria-expanded", String(open));
    head.append(...labelNodes, el("span", "ec-group-n", String(count)), el("span", "scaret ec-group-caret"));
    head.addEventListener("click", () => setSectionExpanded(secId, head.getAttribute("aria-expanded") !== "true"));
    return head;
  }

  // A section heading - an ACCORDION control: type-ink dot + label + the
  // honest, free count + a caret. Clicking toggles the section's cards via
  // setSectionExpanded (a separate data-secfold visibility channel - the
  // `hidden` property stays exclusively owned by the more-fold/bundle
  // mechanics, so the two systems can never fight over one bit). data-total
  // carries the section's full count so the language filter can retitle the
  // count ("8 of 12") without re-deriving it from the DOM.
  function sectionHead(label: string, count: number, colorVar: string, secId: string, memKey: string, open: boolean): HTMLElement {
    const dot = el("span", "ec-dot ec-group-dot");
    dot.style.setProperty("--dot", `var(${colorVar})`);
    dot.setAttribute("aria-hidden", "true");
    return accHead("ec-group ec-group-btn", [dot, el("span", "ec-group-label", label.toUpperCase())], count, secId, memKey, open);
  }

  // One rabbit-hole BUCKET disclosure ("Sources inside this text - 2"). The
  // heading rides the SAME data-acc/data-sec channel as the verse accordion,
  // so setSectionExpanded (open-trail protection), revealRow (replay / Explore
  // Open auto-expand) and annotateLang (language-filter "1 of 2" retitles) all
  // govern it unchanged. Buckets are COLLAPSED by default: the center column
  // is for reading - the right Explore panel projects these same buckets as
  // the browse surface, and the taken path always stays visible.
  function bucketHead(label: string, count: number, secId: string, memKey: string, open: boolean, key: string, cited: boolean): HTMLElement {
    const head = accHead(
      `cites-bucket cites-bucket-btn${cited ? " cites-bucket-cited" : ""}`,
      [el("span", "cites-bucket-label", label)],
      count,
      secId,
      memKey,
      open,
    );
    head.dataset.bucket = key;
    return head;
  }

  // Open/close one accordion section. Collapsing NEVER hides the active trail:
  // an open row, a row holding an open descendant, or a bundle header whose
  // members hold an open row all stay visible (the exact protection
  // setBundleExpanded gives bundle members). recordChoice: only a reader's own
  // heading click is remembered in the session memory - programmatic surfacing
  // (revealRow: replay, Continue) opens the DOM without overwriting a section
  // the reader deliberately collapsed.
  function setSectionExpanded(secId: string, open: boolean, recordChoice = true): void {
    const head = document.querySelector(`[data-acc="${attrEsc(secId)}"]`);
    if (head instanceof HTMLElement) {
      head.classList.toggle("open", open);
      head.setAttribute("aria-expanded", String(open));
      if (recordChoice && head.dataset.seckey) sectionMemory.set(head.dataset.seckey, open);
    }
    document.querySelectorAll(`[data-sec="${attrEsc(secId)}"]`).forEach((m) => {
      const node = m as HTMLElement;
      if (open) {
        node.removeAttribute("data-secfold");
        return;
      }
      if (node.classList.contains("open") || node.querySelector(".srow.open")) return;
      const bundleId = node.dataset.bundleHead;
      if (
        bundleId &&
        document.querySelector(
          `.srow.ec-bundled.open[data-bundle="${attrEsc(bundleId)}"], .srow.ec-bundled[data-bundle="${attrEsc(bundleId)}"] .srow.open`,
        )
      ) {
        return;
      }
      node.setAttribute("data-secfold", "");
    });
  }

  // Surface a row through EVERY fold layer before (or as) it opens: collapsed
  // accordion section, "Show more" tail, collapsed bundle, and the language
  // filter. One helper so no opener can forget a layer - used by toggleRow (the
  // one place rows open), replayPath, and the Explore panel's Continue.
  function revealRow(row: HTMLElement): void {
    if (row.dataset.sec) setSectionExpanded(row.dataset.sec, true, false);
    if (row.dataset.more) expandMore(row.dataset.more);
    if (row.dataset.bundle) {
      setBundleExpanded(row.dataset.bundle, true);
      document
        .querySelector(`.ec-bundle[data-bundle-head="${attrEsc(row.dataset.bundle)}"]`)
        ?.removeAttribute("data-langhide");
    }
    row.removeAttribute("data-langhide");
  }

  // ---- the "Show sources" display-language filter ---------------------------
  // Mode changes ANNOTATE the loaded panels in place (data-langhide + counts +
  // one calm note) - they never rebuild them. Rebuilding would destroy the open
  // .srow chain, and that chain IS the journey (DOM-path-as-truth). Only the
  // Explore rail - a projection - rebuilds, via its existing refreshExplore.

  // Would this row be hidden by the active mode? Open rows (or rows holding an
  // open descendant) are NEVER hidden - the journey always survives a mode
  // switch. Unknown availability (no data-hasen/hashe stamped) never hides.
  function rowLangHidden(node: HTMLElement, mode: SourceDisplayMode): boolean {
    if (mode === "default") return false;
    if (node.classList.contains("open") || node.querySelector(".srow.open")) return false;
    if (mode === "english") return node.dataset.hasen === "0";
    return node.dataset.hashe === "0";
  }

  // Annotate ONE container (a verse .sources panel or a rabbit-hole .citelist)
  // in place. Idempotent: re-run on every mode change and after every load.
  function annotateLang(container: HTMLElement): void {
    const mode = getSourceLangMode();
    const rows = [...container.querySelectorAll(":scope > .srow[data-ref]")] as HTMLElement[];
    for (const row of rows) row.toggleAttribute("data-langhide", rowLangHidden(row, mode));

    // A bundle header hides only when EVERY member is language-hidden.
    for (const bh of [...container.querySelectorAll(":scope > .ec-bundle[data-bundle-head]")] as HTMLElement[]) {
      const members = rows.filter((r) => r.dataset.bundle === bh.dataset.bundleHead);
      bh.toggleAttribute("data-langhide", members.length > 0 && members.every((m) => m.hasAttribute("data-langhide")));
    }

    // "Show N more" buttons: a mode can hide part (or all) of a folded tail.
    // Retitle to what a click will actually reveal, and fold the button away
    // entirely when that is zero - a button that visibly does nothing is a lie.
    for (const btn of [...container.querySelectorAll(":scope > .ec-more[data-more-btn]")] as HTMLElement[]) {
      const id = btn.dataset.moreBtn ?? "";
      const governed = [...container.querySelectorAll(`:scope > [data-more="${attrEsc(id)}"]`)] as HTMLElement[];
      const revealable = governed.filter((m) => !m.hasAttribute("data-langhide")).length;
      btn.toggleAttribute("data-langhide", revealable === 0);
      if (revealable > 0) {
        btn.textContent = `Show ${revealable} more in this section`;
        const under = btn.dataset.secLabel ? ` under ${btn.dataset.secLabel}` : "";
        btn.setAttribute("aria-label", `Show ${revealable} more${under}`);
      }
    }

    // Accordion heading counts ("8 of 12") + a per-section empty note when a
    // mode leaves a section with nothing to show.
    container.querySelectorAll(":scope > .ec-langempty").forEach((n) => n.remove());
    for (const headEl of [...container.querySelectorAll(":scope > [data-acc]")] as HTMLElement[]) {
      const secId = headEl.dataset.acc ?? "";
      const total = Number(headEl.dataset.total ?? "0");
      const secRows = rows.filter((r) => r.dataset.sec === secId);
      const shown = secRows.filter((r) => !r.hasAttribute("data-langhide")).length;
      const nEl = headEl.querySelector(".ec-group-n");
      if (nEl) nEl.textContent = shown === total ? String(total) : `${shown} of ${total}`;
      if (mode !== "default" && total > 0 && shown === 0) {
        const note = el(
          "div",
          "ec-langempty",
          mode === "english" ? "No English text available in this section." : "No Hebrew text available in this section.",
        );
        note.dataset.sec = secId;
        if (headEl.getAttribute("aria-expanded") !== "true") note.setAttribute("data-secfold", "");
        headEl.after(note);
      }
    }

    // The ONE calm panel note - transparency without per-card warnings.
    container.querySelector(":scope > .langnote")?.remove();
    const hiddenN = rows.filter((r) => r.hasAttribute("data-langhide")).length;
    if (mode !== "default" && hiddenN > 0) {
      const word = mode === "english" ? "English" : "Hebrew";
      const allHidden = hiddenN === rows.length;
      const note = langNote(
        allHidden
          ? `No ${word} text is available for these sources yet. Switch to Default to see all sources.`
          : `${word} only: ${hiddenN} ${hiddenN === 1 ? "source" : "sources"} hidden because no ${word} text is available.`,
        () => {
          // applySourceLang re-annotates and REMOVES this note (nothing is hidden
          // in default mode) - the activated button vanishes. Keep the keyboard /
          // screen-reader position in the list: focus the list container itself
          // (the collapseAll idiom), never let focus fall to <body>.
          applySourceLang("default");
          container.tabIndex = -1;
          container.focus();
        },
      );
      container.prepend(note);
    }
  }

  // Re-apply the active mode to everything on screen (html attribute drives the
  // pure-CSS title display; annotation drives the list filtering).
  function refreshSourceLang(): void {
    const mode = getSourceLangMode();
    if (mode === "default") delete document.documentElement.dataset.srclang;
    else document.documentElement.dataset.srclang = mode;
    // scent tiers count through the SAME mode filter as the lists - repaint
    for (const ref of verseScent.keys()) paintScent(ref);
    document.querySelectorAll(".sources, .citelist").forEach((c) => {
      if (c instanceof HTMLElement) annotateLang(c);
    });
    refreshExplore();
  }

  // The single entry point a mode change goes through (the reader control AND
  // every "Show all" escape hatch): set + persist + sync control buttons +
  // re-annotate + tell screen readers.
  function applySourceLang(mode: SourceDisplayMode): void {
    setSourceLangMode(mode);
    void persistSourceLang(mode);
    document.querySelectorAll(".rc-lang[data-mode]").forEach((b) => {
      b.setAttribute("aria-pressed", String((b as HTMLElement).dataset.mode === mode));
    });
    refreshSourceLang();
    announce(
      mode === "default"
        ? "Showing all sources."
        : mode === "english"
          ? "English only: sources without English text are hidden."
          : "Hebrew only: showing Hebrew source names.",
    );
  }

  async function persistSourceLang(mode: SourceDisplayMode): Promise<void> {
    try {
      await updatePrefs({ sourceLang: mode });
    } catch {
      /* persistence failure - the mode still applies this session */
    }
  }

  // A source CARD. `.ec` classes ride ON the `.srow` container; `.srowhead` and
  // `.sbody` stay DIRECT children (replayPath contract; the action row is just
  // one more direct sibling). The learner card is deliberately MINIMAL: Hebrew
  // title, English title, and what you can DO (Preview / Continue / Side Path -
  // the actions hide once the card is open). The section heading carries the
  // context (type/era), so per-card type labels, relationship chips and reason
  // lines are debug-only inspection vocabulary (isSourceDebug). The honest
  // relationship still reaches screen readers via the head's aria-label and
  // drives Peek/journey projections - computing it is never skipped.
  function sourceCard(item: Item, depth: number, opts: CardOpts = {}): HTMLElement {
    const row = el("div", depth === 1 ? "srow ec" : "srow ec ec-cited");
    row.dataset.ref = item.ref;
    const label = itemLabel(item);
    row.dataset.label = label;
    row.dataset.linkType = item.linkType;
    row.dataset.category = item.category;
    if (item.commentatorHe) row.dataset.he = item.commentatorHe;
    // Stamp the era/time-hop inputs (additive; the replayPath selector contract reads
    // ref/linkType/category/he and never these, so this cannot affect replay). compDate
    // rides on the getLinks payload already in hand - no extra fetch. Real value or absent.
    if (item.compDate) row.dataset.compdate = JSON.stringify(item.compDate);
    if (item.era) row.dataset.era = item.era;
    // EFFECTIVE language availability (curated override -> provider boolean ->
    // unknown), stamped as facts the display-mode filter reads. Absent = unknown
    // = never hidden (accuracy-lock: hide only on validated knowledge).
    const effEn = hasEnglishAvailable(item);
    const effHe = hasHebrewAvailable(item);
    if (effEn !== null) row.dataset.hasen = effEn ? "1" : "0";
    if (effHe !== null) row.dataset.hashe = effHe ? "1" : "0";

    const type = getSourceType(item.category);
    const rel = getRelationship(item.linkType, item.category);
    row.style.setProperty("--tc", `var(${type.colorVar})`); // the type ink, for the card spine

    // Direction honesty: a relationship verb ("comments on this", "cites this")
    // reads correctly when the anchor is a VERSE (depth 1) - the card source acts
    // on the verse. But in the rabbit hole (depth > 1, anchored on a SOURCE), the
    // direction can invert (Rashi cites Menachos, not the reverse). A
    // commentary-shaped ref ("X on Y") still reliably comments on what it is on;
    // anything else gets a neutral, non-directional phrase so we never assert a
    // backwards citation. Honest > nuanced.
    const onShaped = / on /i.test(item.ref);
    const relText = opts.relOverride ?? (depth > 1 && !onShaped ? "cross-reference" : rel.label);

    const body = el("div", "sbody");
    const bodyId = nextId("sbody");
    body.id = bodyId;

    const head = el("button", "srowhead");
    head.type = "button";
    head.setAttribute("aria-expanded", "false");
    head.setAttribute("aria-controls", bodyId);
    // include the visible Hebrew name so voice-control users can activate by what they see
    const hePrefix = item.commentatorHe ? `${item.commentatorHe}, ` : "";
    // quiet memory: this exact ref was opened before (any session). Recognition,
    // not achievement - a muted word, no badge, no count.
    const seenBefore = exploredSet.has(item.ref) || sessionOpened.has(item.ref);
    head.setAttribute(
      "aria-label",
      `${hePrefix}${label} - ${relDisplay(relText)}${seenBefore ? ", seen before" : ""}, open in your journey`,
    );

    const dot = el("span", "ec-dot");
    dot.style.setProperty("--dot", `var(${type.colorVar})`);
    dot.setAttribute("aria-hidden", "true");

    const cardBody = el("span", "ec-body");
    if (item.commentatorHe) {
      const he = el("span", "ec-he", item.commentatorHe);
      he.lang = "he";
      he.dir = "rtl";
      cardBody.append(he);
    }
    cardBody.append(el("span", "ec-en", label));
    cardBody.append(
      el(
        "span",
        "ec-context",
        sourceContextLine({
          typeLabel: type.label,
          relationshipLabel: relDisplay(relText),
          anchorLabel: formatContextAnchor(opts.anchorName),
        }),
      ),
    );
    if (seenBefore) {
      const seen = el("span", "ec-seen", "seen");
      seen.setAttribute("aria-hidden", "true"); // the head aria-label already says it
      cardBody.append(seen);
    }
    if (isSourceDebug()) {
      const metaRow = el("span", "ec-meta");
      metaRow.append(document.createTextNode(type.label), relChip(relText, "rel-chip-card"));
      cardBody.append(metaRow);
      // The honest "why this card is here" line, from the real relationship + the
      // real anchor only (reasonText never invents a direction).
      const reason = opts.reasonOverride ?? (opts.anchorName ? reasonText(relText, opts.anchorName) : null);
      if (reason) cardBody.append(el("span", "ec-why", reason));
    }

    head.append(dot, cardBody, el("span", "scaret ec-caret"));
    head.addEventListener("click", () => void toggleRow(row, head, body, item, depth));

    row.append(head);

    // The card's explicit action row (visible while CLOSED; the open card has its
    // own context). ONE reading model: Open is the primary verb (no separate
    // Preview/peek - that mode was removed); Desk and Bookmarks are overlays that
    // never touch the trail.
    const acts = el("div", "ec-acts");
    const desk = actBtn("ec-act ec-desk", isOnDesk(item.ref) ? "On desk" : "Add to Desk", deskTip(label), (btn) => {
      addDeskIdentity(toDeskItem(item), btn);
    });
    if (isOnDesk(item.ref)) markDeskButton(desk, label);
    const bookmark = actBtn("ec-act ec-bookmark", bookmarkedRefs.has(item.ref) ? "Bookmarked" : "Bookmark", bookmarkTip(label), (btn) => {
      void bookmarkIdentity(toBookmarkItem(item), btn);
    });
    bookmark.setAttribute("aria-pressed", String(bookmarkedRefs.has(item.ref)));
    if (bookmarkedRefs.has(item.ref)) markBookmarkButton(bookmark, label);
    acts.append(
      actBtn("ec-act ec-act-go", "Open", openTip(label), () => {
        if (!row.classList.contains("open")) head.click();
      }),
      desk,
      bookmark,
    );
    row.append(acts);

    row.append(body);
    return row;
  }

  // ---- in-text citation pills (Tranche 5) -----------------------------------
  // The anchors inside a source's own text become in-app peeks - ONLY when the
  // decoded ref is gated by this source's real accepted links (the same items
  // its citelist shows). The sanitize layer already neutralized relative hrefs
  // into data-mm-cite, so an ungated anchor is inert text, never a 404.
  // ONE pill behavior for both pill kinds (Sefaria-anchor pills AND find-refs
  // text pills): role/tabbing/aria + the peek opener with the same guard, so
  // the same citation can never behave differently depending on whether the
  // upstream HTML happened to ship an anchor.
  function wireCitePillBehavior(elm: HTMLElement, ariaLabel: string, peek: () => void): void {
    elm.setAttribute("role", "button");
    elm.tabIndex = 0;
    elm.setAttribute("aria-label", ariaLabel);
    const open = (e: Event): void => {
      e.preventDefault();
      e.stopPropagation(); // a citation tap must never toggle the source card
      peek();
    };
    elm.addEventListener("click", open);
    elm.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") open(e);
    });
  }

  function peekLinkedCitation(match: Item, ownRow: HTMLElement, relationshipLabel: string): void {
    openPeek({
      ref: match.ref,
      label: itemLabel(match),
      labelHe: match.commentatorHe,
      relationshipLabel,
      colorVar: getSourceType(match.category).colorVar,
      onOpenHere: () => continueFromRow(ownRow, match.ref),
    });
  }

  // An in-text pill must be HONEST about the bucket its matched ref belongs to: a
  // SOLID `.citepill` (no modifier) means ONLY the dual-gate `cited` bucket. A real
  // link the source's Hebrew did NOT name is the quieter `.citepill--related` and
  // reads "related source" / peeks "cross-reference" - the SAME word its Explore
  // card shows (BUCKET_META.otherConnected.rel). NEVER an over-claimed "cited". The
  // buckets are read from `citeData` (stamped by loadCites before any wiring);
  // default to related-never-cited when they are not known yet.
  function citePillVocab(engineRow: HTMLElement, ref: string): { related: boolean; ariaRel: string; peekRel: string } {
    const d = citeData.get(engineRow);
    if (d?.cited.some((it) => refsOverlap(it.ref, ref))) {
      return { related: false, ariaRel: "cited in this text", peekRel: "cited here" };
    }
    if (d?.commentaryOn.some((it) => refsOverlap(it.ref, ref))) {
      return { related: true, ariaRel: "commentary on this", peekRel: relDisplay("comments on this") };
    }
    return { related: true, ariaRel: "related source", peekRel: "cross-reference" };
  }

  // Wire a set of in-text anchors into in-app citation pills. `engineRow` is the
  // hidden center .srow that owns the click behavior (peek + "Open here" drive
  // the REAL engine), so the SAME wiring serves both the engine's own text and
  // the wide Explore reader's projection of it.
  function wireCiteAnchors(anchors: Iterable<HTMLAnchorElement>, items: Item[], engineRow: HTMLElement): void {
    for (const a of anchors) {
      if (a.dataset.mmWired) continue;
      const raw = a.dataset.mmCite ?? a.getAttribute("href");
      if (!raw) continue;
      const decoded = refFromSefariaUrl(raw);
      if (!decoded) continue;
      const match = items.find((it) => refsOverlap(decoded, it.ref));
      if (!match) continue; // ungated: never invent in-app behavior
      const v = citePillVocab(engineRow, match.ref);
      a.dataset.mmWired = "1";
      a.classList.add("citepill");
      if (v.related) a.classList.add("citepill--related");
      a.removeAttribute("href");
      a.removeAttribute("target");
      a.removeAttribute("rel");
      wireCitePillBehavior(a, `${itemLabel(match)} - ${v.ariaRel}; peek without leaving`, () =>
        peekLinkedCitation(match, engineRow, v.peekRel),
      );
    }
  }
  function wireCitationAnchors(ownRow: HTMLElement, items: Item[]): void {
    wireCiteAnchors(ownRow.querySelectorAll<HTMLAnchorElement>(":scope > .sbody .stext a"), items, ownRow);
  }

  // Promote a peeked citation into a real Open INSIDE the row that cited it -
  // by clicking its inline card (the real mechanism; we never fabricate a dive).
  function continueFromRow(row: HTMLElement, ref: string): void {
    const citelist = row.querySelector(":scope > .sbody > .cites > .citelist");
    const target = citelist?.querySelector(`.srow[data-ref="${attrEsc(ref)}"]`);
    if (!(target instanceof HTMLElement)) return;
    revealRow(target);
    const head = target.querySelector(":scope > .srowhead");
    if (!target.classList.contains("open") && head instanceof HTMLElement) head.click();
  }

  // ---- find-refs text enhancement (v7) --------------------------------------
  // Resolve the FREE-TEXT citations in a source's English rendering through
  // Sefaria's own linker (find-refs) and turn them into pills. A citation that
  // matches one of THIS source's accepted links gets the full pill (peek +
  // "Open here" - identical to wireCitationAnchors); a resolved-but-unlinked
  // MENTION ("cf. Exod. 15.17" in a translator's note) gets a peek-only
  // mention pill: the target text is real and getText-validated, but there is
  // no card - and deliberately no journey step - so the audited replay
  // contract is untouched. Enhancement-only: silent skip on any failure,
  // reading never blocked, one find-refs call per source per session.
  function scheduleSenEnhancementOn(sen: HTMLElement | null, ownRef: string, items: Item[], engineRow: HTMLElement): void {
    if (import.meta.env.MODE === "test" && !FINDREFS_IN_TESTS) return;
    const nav = globalThis.navigator as Navigator & { connection?: { saveData?: boolean } };
    if (nav?.connection?.saveData) return; // a nicety, like scent - skip on data-saver
    if (!sen || sen.dataset.mmRefs) return; // no English / already enhanced
    // pre-gate: no citation-shaped token -> no call. Covers chapter:verse
    // ("20:4"), JPS dotted ("15.17"), AND daf refs ("Menachot 43b") - a
    // Talmud-only note must not silently lose the feature.
    if (!/\d+[.:]\d+|\d+[ab]\b/.test(sen.textContent ?? "")) return;
    sen.dataset.mmRefs = "pending";
    const idle = (cb: () => void): void => onIdle(cb, FINDREFS_IDLE_FALLBACK_MS);
    idle(() => {
      // the reader may have remounted while we idled - never spend find-refs
      // requests on a detached node
      if (!sen.isConnected) return;
      void enhanceSen(engineRow, sen, ownRef, items);
    });
  }
  // The engine wires its OWN text; the wide reader projects the same pills onto
  // its copy via scheduleSenEnhancementOn (clicks route back to `engineRow`).
  function scheduleSenEnhancement(ownRow: HTMLElement, ownRef: string, items: Item[]): void {
    scheduleSenEnhancementOn(ownRow.querySelector<HTMLElement>(":scope > .sbody > .stext > .sen"), ownRef, items, ownRow);
  }

  async function enhanceSen(ownRow: HTMLElement, sen: HTMLElement, ownRef: string, items: Item[]): Promise<void> {
    // Offsets are against the EXACT string submitted - capture it, and drop the
    // whole pass if the node was re-rendered while the task ran (the fresh
    // .sen carries no stamp, re-schedules itself, and hits the session cache).
    const bodyText = sen.textContent ?? "";
    const found = await findRefsInText(`${ownRef}::en`, bodyText);
    if (!sen.isConnected || sen.textContent !== bodyText) return;
    if (!found) {
      sen.dataset.mmRefs = "1";
      return;
    }
    // Right-to-left so DOM wrapping never invalidates pending offsets, with an
    // explicit overlap guard (wrap only left of everything already wrapped).
    const rows = [...found.results].sort((a, b) => b.startChar - a.startChar);
    let lastWrappedStart = Infinity;
    for (const r of rows) {
      if (r.linkFailed || r.refs.length !== 1) continue; // ambiguous -> never guess
      if (r.endChar > lastWrappedStart) continue;
      const ref = r.refs[0];
      const data = found.refData[ref];
      if (!data) continue;
      if (refsOverlap(ref, ownRef)) continue; // self-citation = noise
      // The ONE taxonomy gate every link goes through - mentions obey it
      // identically (Commentary mentions face the commentator allowlist too).
      const accepted = acceptSourceLink({
        ref,
        category: data.primaryCategory,
        commentator: parseCanonicalRef(ref)?.workTitle ?? ref,
        commentatorHe: null,
        anchorRef: "",
        linkType: "",
        hasEn: null,
        hasHe: null,
        anchorRefExpanded: null,
        compDate: null,
        era: null,
      });
      if (!accepted) continue;
      const span = findTextSpan(sen, r.startChar, r.endChar);
      if (!span) continue; // crosses element boundaries -> skip, never half-wrap
      if (span.node.parentElement?.closest("a, .citepill")) continue;
      // ACCURACY-LOCK backstop: the DOM slice must equal the matched text verbatim.
      if (span.node.data.slice(span.startOff, span.endOff) !== r.text) continue;
      const match = items.find((it) => refsOverlap(ref, it.ref)) ?? null;
      wrapCitation(span, r.text, ref, data, match, ownRow);
      lastWrappedStart = r.startChar;
    }
    sen.dataset.mmRefs = "1";
  }

  function wrapCitation(
    span: { node: Text; startOff: number; endOff: number },
    text: string,
    ref: string,
    data: { heRef: string; primaryCategory: string },
    match: Item | null,
    ownRow: HTMLElement,
  ): void {
    const target = span.node.splitText(span.startOff);
    target.splitText(text.length);
    let pill: HTMLElement;
    if (match) {
      // Same honesty rule as the anchored path: only a dual-gate `cited` ref wears
      // the solid "cited" pill; an otherConnected/commentaryOn link named in the
      // text is the quieter "related" pill - never an over-claimed citation.
      const v = citePillVocab(ownRow, match.ref);
      pill = el("span", v.related ? "citepill citepill--related" : "citepill");
      wireCitePillBehavior(pill, `${itemLabel(match)} - ${v.ariaRel}; peek without leaving`, () =>
        peekLinkedCitation(match, ownRow, v.peekRel),
      );
    } else {
      pill = el("span", "citepill citepill--mention");
      wireCitePillBehavior(pill, `${ref} - mentioned in this text; peek without leaving`, () =>
        openPeek({
          ref,
          label: ref,
          labelHe: data.heRef,
          relationshipLabel: "mentioned in this text",
          colorVar: getSourceType(data.primaryCategory).colorVar,
        }),
      );
    }
    target.parentNode?.insertBefore(pill, target);
    pill.append(target);
  }

  async function toggleRow(
    row: HTMLElement,
    head: HTMLElement,
    body: HTMLElement,
    item: Item,
    depth: number,
  ): Promise<void> {
    const open = row.classList.toggle("open");
    if (open) {
      // ACCORDION (v7): one open sibling per level. The open tree IS the one
      // linear chain the route hash encodes - so step chips can never flip
      // when branch-hopping, and a history restore is lossless. Bundle
      // members are untouched (hidden members are CLOSED; the selector only
      // matches .open). The sibling close happens INSIDE this same toggle,
      // before its single setTrail - one user action, one trail write, one
      // history entry.
      const host = row.parentElement;
      if (host) {
        for (const sib of host.querySelectorAll(":scope > .srow.open")) {
          if (sib !== row && sib instanceof HTMLElement) closeRowTree(sib);
        }
      }
      closeOtherVerseChains(row);
    }
    // Belt-and-suspenders for the visibility invariant: every programmatic
    // opener (replay, Explore Continue, bundles) un-folds first, but enforcing
    // it HERE - the one place a row opens - means NO fold (more-tail, collapsed
    // accordion section, or the language filter) can ever strand an open trail
    // row invisible, no matter who clicks the head.
    if (open) revealRow(row);
    head.setAttribute("aria-expanded", String(open));
    // Keep the language-filter chrome HONEST through trail changes: opening a
    // filtered row un-hides it (revealRow), closing it makes it filterable
    // again - either way the panel's "x of y" counts, langnote and empty-notes
    // must be recomputed, or a note can claim a section is empty while an open
    // card sits under it. annotateLang is idempotent and protects open rows.
    // On close it runs AFTER collapseWithin so it sees the settled state.
    const listHost = row.parentElement;
    const reannotate = (): void => {
      if (listHost instanceof HTMLElement && (listHost.classList.contains("sources") || listHost.classList.contains("citelist"))) {
        annotateLang(listHost);
      }
    };
    if (!open) {
      collapseWithin(row); // also close any open descendants so depth/trail match the DOM
      reannotate();
      setTrail(row.parentElement);
      return;
    }
    reannotate();
    // Record the open BEFORE setTrail: setTrail repaints the journey rail (incl.
    // the session recap), so adding after it left the recap one open behind.
    if (!restoring()) appendSessionTrailFromRow(row, pendingTrailSurface, false);
    sessionOpened.add(item.ref);
    maybeNudgeDesk();
    setTrail(row);
    // Restore walks (cold replay, Back/Forward, side-path) drive this same
    // handler - they get ONE landing announcement from their bracket owner,
    // never a per-step "Opened X" barrage, and they must not write persistent
    // explored-marks for steps the user merely traversed through.
    if (!restoring()) {
      const d = trailChain(row).rows.length;
      announce(`Opened ${item.commentator}. ${d} ${d === 1 ? "step" : "steps"} deep.`);
      void markExplored(item.ref).then(refreshStats);
    }
    if (body.dataset.loaded === "1" || body.dataset.loading === "1") {
      reveal(body);
      return;
    }
    body.dataset.loading = "1";
    await renderBody(body, item, depth);
    if (row.classList.contains("open")) reveal(body);
  }

  async function renderBody(body: HTMLElement, item: Item, depth: number): Promise<void> {
    body.replaceChildren(loadingRow());
    try {
      const t = await getText(item.ref);
      const cites = el("div", "cites");
      body.replaceChildren(textBlock(t.he, t.en), sattribEl(t), cites);
      body.dataset.loaded = "1";
      if (depth < MAX_AUTO_DEPTH) {
        void loadCites(cites, item.ref, depth, t.he);
      } else {
        cites.remove();
        // The depth cap means no citelist (nothing to "Open here" into), but
        // text mentions still resolve - peek-only by construction.
        const ownRow = body.closest(".srow");
        if (ownRow instanceof HTMLElement) scheduleSenEnhancement(ownRow, item.ref, []);
      }
    } catch (err) {
      body.replaceChildren(errorRow(err, () => void renderBody(body, item, depth)));
    } finally {
      body.dataset.loading = "";
    }
  }

  // RESCUE a dead-end source (no catalogued Sefaria links) by recovering the
  // pesukim it quotes VERBATIM. quoteResolver proves each match by exact
  // containment (search rank alone is never trusted), so these are genuine mareh
  // makom, not guesses. v1 scope = Tanakh verses only: cleanly classifiable via
  // the canonical works table, always in-taxonomy, and the dominant quote kind.
  // Each verse gets a UNIQUE commentator label so bundleByCommentator never
  // collapses two distinct pesukim into one card. Returns Items ready for the
  // normal "What this text cites" bucket - they become openable trail rows like
  // any other (continueByRef / replay / panel projection all unchanged).
  async function recoverQuotedItems(selfRef: string, sourceHe: string, exclude: ReadonlySet<string>): Promise<Item[]> {
    // PRIMARY classical sources a commentator actually quotes. Restricting to these
    // is the key PRECISION guard: a later derivative work (Chasidut, Jewish Thought,
    // a much-later commentary) often repeats the same pasuk/maamar verbatim, so an
    // unrestricted search would mis-link e.g. Abarbanel (15th c.) to an 18th-c.
    // prayer book that quotes the same verse. Higher priority = more primary; it
    // breaks ties toward the source actually being quoted (the pasuk, not a repeater).
    const CATEGORY_PRIORITY: Record<string, number> = {
      Tanakh: 5,
      Talmud: 4,
      Midrash: 3,
      Mishnah: 2,
      Tosefta: 1,
    };
    // The source's OWN identity, to never "recover" a piece of itself or the very
    // pasuk it comments on. workTitle = the ref minus its trailing numeric address.
    const workTitle = (r: string): string => r.replace(/[\s,]*\d[\d:.\sab.-]*$/, "").trim();
    const selfWork = workTitle(selfRef);
    // The base verse(s) the source comments on (where the learner came FROM): strip
    // the "<Commentator> on " prefix, then parse the trailing "<Book> c:v" - which
    // may be a RANGE ("Genesis 1:1-5" or "1:1-2:3"). Splitting on " on " first is
    // essential (a naive [A-Za-z ] book pattern swallows the "on"); accepting the
    // range tail is essential too (a range-anchored commentary that restates one of
    // its own pesukim must not surface that pasuk as something it "cites").
    const basePart = selfRef.includes(" on ") ? selfRef.split(" on ").pop()! : selfRef;
    const bm = basePart.match(/([^,]+?) (\d+):(\d+)(?:-(?:(\d+):)?(\d+))?$/);
    const baseBook = bm ? lookupWork(bm[1].trim())?.en ?? bm[1].trim() : null;
    const baseCh = bm ? +bm[2] : null;
    const baseV1 = bm ? +bm[3] : null;
    const baseEndCh = bm && bm[4] ? +bm[4] : baseCh; // cross-chapter end chapter (or same)
    const baseV2 = bm && bm[5] ? +bm[5] : baseV1; // end verse (or same as start)
    // Is candidate ref a pasuk INSIDE the base range? Then it is the verse the source
    // is built on, not a citation - exclude it (handles single verse + both ranges).
    const inBaseRange = (r: string): boolean => {
      if (!baseBook || baseCh === null) return false;
      const v = r.match(/^([A-Za-z][A-Za-z ]*?) (\d+):(\d+)$/);
      if (!v || (lookupWork(v[1].trim())?.en ?? v[1].trim()) !== baseBook) return false;
      const c = +v[2];
      const ve = +v[3];
      return (
        (baseEndCh === baseCh && c === baseCh && ve >= baseV1! && ve <= baseV2!) ||
        (baseEndCh !== baseCh &&
          ((c === baseCh && ve >= baseV1!) || (c > baseCh && c < baseEndCh!) || (c === baseEndCh && ve <= baseV2!)))
      );
    };
    // Tanakh proximity (within the priority tier): prefer the verse nearest the
    // source's own location, so a phrase in two pesukim resolves in context.
    const anchorBook = baseBook;
    const anchorCh = baseCh;

    let resolved: { ref: string; phrase: string }[] = [];
    try {
      resolved = await resolveQuotedRefs({
        sourceHe,
        search: (p) => searchRefs(p, 12),
        // Cheap shape gate: a base source ref (has a numeric address, not a "X on Y"
        // commentary, not the source itself or an already-linked ref).
        prefilter: (r) =>
          !/ on /i.test(r) && /\s\d/.test(r) && r !== selfRef && !exclude.has(r) && workTitle(r) !== selfWork,
        // Classify from SEFARIA's own response (category + heRef) - covers ALL
        // primary source kinds, gated by the app's allowlist. Returns the text for
        // the containment proof + a score (category priority, then Tanakh proximity).
        classify: async (r) => {
          if (inBaseRange(r)) return null; // a verse the source is built ON is where you came FROM
          // getTextForRecovery is budget-capped + never throws (null = skip), so the
          // candidate-text fan-out can never drain the shared budget core reading needs.
          const t = await getTextForRecovery(r);
          if (!t) return null;
          const cat = t.primaryCategory;
          const tier = cat ? CATEGORY_PRIORITY[cat] : undefined;
          if (tier === undefined) return null; // not a primary classical source
          const link: SourceLink = {
            ref: r,
            category: cat as string,
            commentator: r,
            commentatorHe: t.heRef ?? null,
            anchorRef: selfRef,
            linkType: "",
            hasEn: null,
            hasHe: null,
            anchorRefExpanded: null,
            compDate: null,
            era: null,
          };
          if (!acceptSourceLink(link)) return null; // honor the app's source allowlist
          const vm = r.match(/^([A-Za-z][A-Za-z ]*?) (\d+):(\d+)$/);
          const sameBook = !!vm && anchorBook !== null && (lookupWork(vm[1].trim())?.en ?? vm[1].trim()) === anchorBook;
          const proximity = sameBook && anchorCh !== null ? 100 - Math.min(99, Math.abs(+vm![2] - anchorCh)) : 0;
          return { he: t.he, score: tier * 1000 + proximity };
        },
        // Genuine quotes are sparse and can sit late in a long discursive commentary,
        // so scan many spans; keep candidates-per-span tight (search is best-ranked,
        // the real source is near the top) to bound the candidate-text fan-out. The
        // recovery getText cap is the hard backstop; recovery only runs when no
        // in-text citation was found.
        maxSpans: 20,
        maxCandidates: 4,
      });
    } catch {
      resolved = [];
    }
    // Synthesize Items (getText is cached from classify). commentatorHe = Sefaria's
    // own heRef, so the card is bilingual for ANY source kind; the ref is a unique
    // label so bundleByCommentator never collapses two distinct sources.
    const items: Item[] = [];
    for (const { ref } of resolved) {
      let t;
      try {
        t = await getText(ref);
      } catch {
        continue;
      }
      if (!t.primaryCategory) continue;
      items.push({
        ref,
        commentator: ref,
        commentatorHe: t.heRef ?? null,
        linkType: "",
        category: t.primaryCategory,
        hasEn: null,
        hasHe: null,
      });
    }
    return items;
  }

  // Engine rows whose verbatim-quote recovery is still running. The Explore panel
  // shows a calm "finding..." state for these instead of the leaf dead-end, until
  // recovery settles (then either recovered quotes or the honest leaf).
  const recoveringRows = new WeakSet<HTMLElement>();
  const isRecovering = (node: HTMLElement | null): boolean => node instanceof HTMLElement && recoveringRows.has(node);

  // Append recovered quote rows to a source's citelist as a "What this text cites"
  // bucket. ADDITIVE only - it appends new .srow siblings (replay-safe) and never
  // touches existing open rows, so it cannot disturb a chain the learner has
  // already started. Called only when the dual gate found NO cited bucket, so this
  // is always the sole cited bucket (no duplicate-heading collision).
  function appendCitedRows(ownRow: HTMLElement, recovered: Item[], ref: string, depth: number, parentLabel: string): void {
    const host = ownRow.querySelector(":scope > .sbody > .cites");
    if (!(host instanceof HTMLElement)) return;
    let list = host.querySelector<HTMLElement>(":scope > .citelist");
    if (!list) {
      list = el("div", "citelist");
      host.replaceChildren(list);
    }
    const secId = nextId("sec");
    const memKey = `${ref}::bucket:cited`;
    const open = sectionMemory.get(memKey) ?? false;
    list.append(bucketHead("What this text cites", recovered.length, secId, memKey, open, "cited", true));
    const els = cardSequence(recovered, depth + 1, {
      relOverride: "cited here",
      anchorName: parentLabel,
      reasonOverride: `Quoted in ${parentLabel}`,
    });
    for (const e of els) {
      e.dataset.bucket = "cited";
      e.dataset.sec = secId;
      if (!open) e.setAttribute("data-secfold", "");
    }
    list.append(...els);
    annotateLang(list);
  }

  // The sources connected to THIS source - the heart of "mareh makom". Three honest
  // buckets, in value order:
  //   1. "What this text cites" - the GENUINE mar'eh makom: refs the source's
  //      own Hebrew text actually quotes, via the dual gate in citations.ts (the
  //      ref's title + location must appear in the text AND be a real linked ref),
  //      PLUS verbatim sources recovered by quoteResolver when the dual gate finds
  //      none. This is the earned "cited here"; if the text cites nothing and
  //      quotes nothing resolvable, this bucket is absent (never fabricated).
  //   2. "Commentaries on this" - meforshim ON this source (refs structurally
  //      "X on <base>"; reliably comment on what they are on).
  //   3. "Where else this comes up" - other cross-references, neutral/non-directional, so
  //      we never assert a backwards citation.
  // All three share ONE .citelist (bucket labels are non-.srow rows) so the
  // deep-link replay `:scope > .srow` selectors are untouched.
  async function loadCites(host: HTMLElement, ref: string, depth: number, sourceHe = ""): Promise<void> {
    if (host.dataset.loading === "1") return;
    host.dataset.loading = "1";
    host.replaceChildren(loadingRow());
    const ownRow = host.closest(".srow");
    const parentLabel = ownRow instanceof HTMLElement ? ownRow.dataset.label ?? ref : ref;
    try {
      const items = groupLinks(await getLinks(ref)).flatMap((g) => g.items);
      const commentaryOn = items.filter((it) => / on /i.test(it.ref));
      const connected = items.filter((it) => !/ on /i.test(it.ref));
      // Dual-gate: which connected refs does the source's OWN text genuinely cite?
      const base = / on /i.test(ref) ? ref.split(/ on /i).pop()?.trim() ?? null : null;
      const citedSet = sourceHe
        ? citedInText(sourceHe, connected.map((it) => ({ ref: it.ref, titleHe: it.commentatorHe })), base)
        : new Set<string>();
      const cited = connected.filter((it) => citedSet.has(it.ref));
      const otherConnected = connected.filter((it) => !citedSet.has(it.ref));

      // Did the dual gate find genuine in-text citations? If NOT (cited empty), we
      // RECOVER the verbatim sources this text quotes (search + exact-containment) -
      // the rescue that lets ANY commentator's quoted sources be followed, not only
      // the ones Sefaria pre-linked. This is the broad win: it runs for every source
      // with no detected in-text citation, NOT just pure dead-ends. Recovery is
      // NON-BLOCKING - render what we have now, append recovered quotes when they
      // land - so a source that already has citations (cited > 0) pays nothing and
      // a source with other links is never delayed. (extractQuotedSpans returns []
      // when the text has no citation cues, so cue-less sources spend zero searches.)
      const willRecover =
        !!sourceHe && cited.length === 0 && (import.meta.env.MODE !== "test" || RECOVER_IN_TESTS);

      // Genuine leaf only if there is nothing now AND nothing to recover. Record the
      // EMPTY result so the Explore panel resolves to a definite, honest leaf state
      // (instead of being stranded on whatever it showed at open time), then stop.
      if (!cited.length && !commentaryOn.length && !otherConnected.length && !willRecover) {
        if (ownRow instanceof HTMLElement) {
          scheduleSenEnhancement(ownRow, ref, []);
          citeData.set(ownRow, { cited: [], commentaryOn: [], otherConnected: [] });
          if (ownRow.isConnected && ownRow === currentNode) refreshExplore();
        }
        host.remove();
        return;
      }

      // Stash the honest buckets so the right-side Explore panel projects the SAME
      // data without a second fetch (keyed by the open .srow that owns these cites).
      if (ownRow instanceof HTMLElement) {
        citeData.set(ownRow, { cited, commentaryOn, otherConnected });
        // In-text citation pills: now that this source's REAL accepted links are
        // known, anchors in its own text whose decoded ref is among them become
        // in-app peeks. Ungated anchors keep hardened external/inert behavior.
        wireCitationAnchors(ownRow, items);
        // ...and the FREE-TEXT citations (no anchor in Sefaria's HTML) resolve
        // through find-refs: linked -> full pill, unlinked -> peek-only mention.
        scheduleSenEnhancement(ownRow, ref, items);
        if (willRecover) recoveringRows.add(ownRow); // panel shows "finding..." until it settles
      }

      const list = el("div", "citelist");
      // Each bucket renders as a collapsed disclosure (bucketHead) + its cards.
      // Members are stamped data-bucket (the earned "cited here" projection
      // reads it) AND data-sec/data-secfold (the shared accordion channel, so
      // replay and Explore "Open" auto-expand a folded bucket). An EMPTY bucket
      // renders NOTHING - the disclosure line IS the bucket's presence.
      const addBucket = (
        heading: string,
        arr: Item[],
        o: { key: "cited" | "commentary" | "connected"; cited?: boolean; relOverride?: string; reasonOverride?: string },
      ): void => {
        if (!arr.length) return;
        const secId = nextId("sec");
        const memKey = `${ref}::bucket:${o.key}`;
        const open = sectionMemory.get(memKey) ?? false; // collapsed default: the center is for reading
        list.append(bucketHead(heading, arr.length, secId, memKey, open, o.key, !!o.cited));
        const els = cardSequence(arr, depth + 1, {
          relOverride: o.relOverride,
          anchorName: parentLabel,
          reasonOverride: o.reasonOverride,
        });
        for (const e of els) {
          e.dataset.bucket = o.key;
          e.dataset.sec = secId;
          if (!open) e.setAttribute("data-secfold", "");
        }
        list.append(...els);
      };
      addBucket("What this text cites", cited, {
        key: "cited",
        cited: true,
        relOverride: "cited here",
        reasonOverride: `Cited in ${parentLabel}`,
      });
      addBucket("Commentaries on this", commentaryOn, {
        key: "commentary",
      });
      addBucket("Where else this comes up", otherConnected, {
        key: "connected",
      });
      host.replaceChildren(list);
      annotateLang(list); // born filtered when a mode is already active
      // Light up "Explore Next" only if these cites belong to the node the panel is
      // currently showing - a slower sibling fetch finishing later must not repaint
      // over (or mis-key against) a node the reader has since moved on to. (The
      // Current Source header reads nothing from citeData, so it is not repainted.)
      // isConnected guards the SHARED .rail-explore against a stale write: a cold
      // remount (parasha change) detaches ownRow while this instance's closure still
      // aliases it as currentNode, so the identity check alone would let a dead
      // instance repaint the live rail. A detached row means "navigated away" -> skip.
      if (ownRow instanceof HTMLElement && ownRow.isConnected && ownRow === currentNode) refreshExplore();

      // NON-BLOCKING quote recovery. The fast path above already rendered; when
      // recovery resolves, append the recovered quotes (additive - never disturbs
      // open rows) and repaint. Errors/empties just clear the "finding..." marker,
      // leaving the honest leaf (or the existing buckets) standing.
      if (willRecover && ownRow instanceof HTMLElement) {
        const row = ownRow;
        const exclude = new Set(items.map((it) => it.ref));
        void recoverQuotedItems(ref, sourceHe, exclude)
          .then((recovered) => {
            recoveringRows.delete(row);
            if (row.isConnected && recovered.length) {
              appendCitedRows(row, recovered, ref, depth, parentLabel);
              const d = citeData.get(row);
              citeData.set(row, {
                cited: [...(d?.cited ?? []), ...recovered],
                commentaryOn: d?.commentaryOn ?? commentaryOn,
                otherConnected: d?.otherConnected ?? otherConnected,
              });
            }
            // isConnected: a detached row means the instance was remounted (parasha
            // change) - never repaint the shared rail from a dead instance, even
            // though its frozen closure still has row === currentNode.
            if (row.isConnected && row === currentNode) refreshExplore();
          })
          .catch(() => {
            recoveringRows.delete(row);
            if (row.isConnected && row === currentNode) refreshExplore();
          });
      }
    } catch (err) {
      host.replaceChildren(errorRow(err, () => void loadCites(host, ref, depth, sourceHe)));
    } finally {
      host.dataset.loading = "";
    }
  }

  // ---- the journey panel (one interactive build, used by both the mobile
  //      drawer and the desktop rail; crumbs step back, side-paths replay) ----

  // Re-render the journey into the desktop rail (if present) AND the open drawer.
  function refreshJourney(): void {
    if (chromeWalk > 0) {
      journeyDirty = true;
      return;
    }
    const rail = document.querySelector(".rail-journey");
    if (rail) rail.replaceChildren(buildJourneyPanelInteractive("rail"));
    if (drawerBody) drawerBody.replaceChildren(buildJourneyPanelInteractive("drawer"));
  }

  let saveChoiceOpen = false;

  function saveCurrentPath(startOver: boolean, trigger: HTMLButtonElement): void {
    if (!sessionTrail.length) return;
    trigger.disabled = true;
    const entries = sessionTrail.map((entry) => ({ ...entry }));
    void saveTrail(`journey:${parashaRef}`, { kind: "session-trail-v2", parashaRef, entries, savedAt: Date.now() }).then(() => {
      saveChoiceOpen = false;
      refreshSavedPathsDrawer();
      // Visible confirmation (was an invisible aria-live announce). The toast rides on
      // document.body, so it survives the startOver re-render below, and showDeskToast
      // routes the message through `announce` itself (no double announcement). Fires in
      // BOTH branches so "Save and start over" now confirms too.
      showDeskToast("Trail saved.", "Open Saved Trails", openSavedPathsSurface, announce);
      if (startOver) {
        startOverSession();
        return;
      }
      refreshJourney();
    });
  }

  function startOverSession(): void {
    clearSessionTrail(false);
    jumpToAnchor();
    refreshJourney();
    renderBottomNav();
  }

  function replaySessionTrailEntry(entry: SessionTrailEntry): void {
    closeAllDrawers();
    if (!entry.path.length) return;
    navHistory.pushRoute(buildRoute(parashaRef, entry.path));
    void applyRoute(entry.path);
  }

  function buildSessionTrailCompass(): HTMLElement {
    const card = el("section", "path-compass");
    card.setAttribute("aria-label", "Session trail");
    const current = sessionTrail.find((entry) => entry.seq === currentSessionTrailSeq) ?? sessionTrail[sessionTrail.length - 1];

    const head = el("div", "path-compass-head");
    head.append(el("div", "path-compass-kicker", "Current"));
    head.append(el("div", "path-compass-title", current.label));
    const meta = el("div", "path-compass-meta");
    if (current.relationshipLabel) meta.append(relChip(relDisplay(current.relationshipLabel)));
    meta.append(el("span", "path-compass-from", `Previous: ${sessionTrail.length >= 2 ? sessionTrail[sessionTrail.length - 2].label : current.anchorLabel}`));
    head.append(meta);
    card.append(head);

    const stats = el("div", "path-compass-stats");
    stats.append(el("span", "path-stat", `${sessionTrail.length} opened`));
    stats.append(el("span", "path-stat", `${new Set(sessionTrail.map((e) => e.ref)).size} unique`));
    card.append(stats);

    const timeline = el("ol", "path-timeline session-trail");
    timeline.setAttribute("aria-label", "Everything opened this session");
    for (const entry of sessionTrail) {
      const isCurrent = entry.seq === current.seq;
      const li = el("li", `path-time-item${isCurrent ? " path-time-current" : ""}`);
      const row = el("button", "path-time-row session-trail-row");
      row.type = "button";
      row.setAttribute("aria-label", `Replay ${entry.label} from your session trail`);
      row.addEventListener("click", () => replaySessionTrailEntry(entry));
      row.append(el("span", "path-time-dot"));
      const txt = el("span", "path-time-text");
      const labRow = el("span", "path-time-labelrow");
      labRow.append(el("span", "path-time-label", entry.label));
      // Subtle era hint NEXT TO (not inside) the source title, only when known (year in tooltip).
      const se = subtleEra(eraOfEntry(entry));
      if (se) {
        const eraEl = el("span", "path-time-era", se.label);
        if (se.year) eraEl.title = se.year;
        labRow.append(eraEl);
      }
      txt.append(labRow);
      const sub = isCurrent ? "current" : `${entry.relationshipLabel} - from ${entry.anchorLabel}`;
      txt.append(el("span", "path-time-sub", sub));
      row.append(txt);
      if (isCurrent) row.setAttribute("aria-current", "true");
      li.append(row);
      timeline.append(li);
    }
    card.append(timeline);

    const actions = el("div", "path-compass-actions");
    const save = button("btn btn-sm path-save", "Save trail", () => {
      saveChoiceOpen = true;
      refreshJourney();
    });
    const start = button("btn btn-sm path-start", "Start over", startOverSession);
    actions.append(save, start);
    card.append(actions);

    if (saveChoiceOpen) {
      const choice = el("div", "path-save-choice");
      choice.append(el("div", "path-save-copy", "Save this trail?"));
      const keep = el("button", "btn btn-sm", "Keep studying");
      keep.type = "button";
      keep.addEventListener("click", () => saveCurrentPath(false, keep));
      const reset = el("button", "btn btn-sm", "Save and start over");
      reset.type = "button";
      reset.addEventListener("click", () => saveCurrentPath(true, reset));
      choice.append(keep, reset);
      card.append(choice);
    }

    return card;
  }

  // ---- Era hint: a SUBTLE era/year shown next to a source title (no new panels, no
  //      second trail). The engine (timeline.ts) is accuracy-locked; show era ONLY when
  //      it is actually known, never the word "unknown" (absence of data = absence of UI).

  // Resolve one open row's EraInfo from the facts it already carries (data-ref /
  // data-category / data-label / data-compdate / data-era). Pure read.
  function stepEra(row: HTMLElement): EraInfo {
    let compDate: [number] | [number, number] | null = null;
    const raw = row.dataset.compdate;
    if (raw) {
      try {
        compDate = validCompDate(JSON.parse(raw)); // reuse the ingest-side guard - one source of truth
      } catch {
        /* malformed JSON -> null, never a guessed year */
      }
    }
    return eraOf({
      category: row.dataset.category ?? null,
      commentator: row.dataset.label ?? null,
      ref: row.dataset.ref ?? null,
      compDate,
      sefariaEra: row.dataset.era ?? null,
    });
  }

  // The subtle hint next to a source title - ONLY when the era is actually known.
  // Unknown era or the undated wellspring (Tanakh) -> null (no UI). Never "unknown".
  function subtleEra(era: EraInfo): { label: string; year: string | null } | null {
    if (era.tier === "unknown" || era.tier === "foundation") return null;
    return { label: era.label, year: era.yearLabel };
  }

  // Era for a session-trail entry (a data object with no live compDate -> override / era.ts
  // / category tier; an un-curated work honestly resolves to unknown -> no hint shown).
  function eraOfEntry(e: SessionTrailEntry): EraInfo {
    return eraOf({ category: e.category, commentator: e.label, ref: e.ref });
  }

  // "Your trail" view mode: the calm List (default - the app as it was) or the visual era
  // Map (opt-in). The Map REPLACES the list when active; the two are never shown together.
  let trailView: "list" | "map" = "list";

  function buildJourneyPanelInteractive(_context: "rail" | "drawer"): HTMLElement {
    const panel = el("div", "journey-panel");
    const first = sessionTrail[0];
    const head = el("div", "journey-head");
    const titleRow = el("div", "journey-titlerow");
    titleRow.append(el("div", "journey-title", "Your trail"));
    if (sessionTrail.length) titleRow.append(buildTrailViewToggle());
    head.append(titleRow);
    head.append(el("div", "journey-anchor", first ? `Started at ${first.anchorLabel}` : "Open a source to begin a journey"));
    panel.append(head);
    panel.classList.add("journey-compact");

    if (!sessionTrail.length) {
      panel.append(studyEmptyState("Start a study trail", "Read or open a source and it will appear here.", ["Pasuk", "Source", "Next source"]));
      return panel;
    }

    panel.append(trailView === "map" ? buildTrailMap() : buildSessionTrailCompass());
    return panel;
  }

  // The List | Map segmented toggle in the trail-panel header.
  function buildTrailViewToggle(): HTMLElement {
    const seg = el("div", "trail-viewtoggle");
    seg.setAttribute("role", "group");
    seg.setAttribute("aria-label", "Trail view");
    const mk = (view: "list" | "map", label: string): HTMLButtonElement => {
      const b = el("button", `tvt-btn${trailView === view ? " tvt-on" : ""}`) as HTMLButtonElement;
      b.type = "button";
      b.textContent = label;
      b.setAttribute("aria-pressed", String(trailView === view));
      b.addEventListener("click", () => {
        if (trailView === view) return;
        trailView = view;
        refreshJourney();
      });
      return b;
    };
    seg.append(mk("list", "List"), mk("map", "Map"));
    return seg;
  }

  // The VISUAL trail (opt-in via the toggle): the session journey as an era-colored path
  // through time - the mockup made real. Replaces the list; never shown beside it. An
  // un-curated source shows a neutral dot + its name only (never the word "unknown").
  function buildTrailMap(): HTMLElement {
    const wrap = el("section", "trail-map");
    wrap.setAttribute("aria-label", "Your trail, visualized through time");
    const ol = el("ol", "tmap-list");
    const originEra = eraOf({ category: "Tanakh", commentator: null, ref: sessionTrail[0]?.anchorRef || parashaRef || null });
    ol.append(tmapNode(sessionTrail[0]?.anchorLabel ?? rootLabel, originEra, true, false, jumpToAnchor));
    for (const entry of sessionTrail) {
      ol.append(tmapNode(entry.label, eraOfEntry(entry), false, entry.seq === currentSessionTrailSeq, () => replaySessionTrailEntry(entry)));
    }
    wrap.append(ol);
    return wrap;
  }

  function tmapNode(name: string, eraInfo: EraInfo, isOrigin: boolean, current: boolean, onClick: () => void): HTMLElement {
    const li = el("li", `tmap-node${current ? " tmap-current" : ""}${isOrigin ? " tmap-origin" : ""}`);
    const btn = el("button", "tmap-btn") as HTMLButtonElement;
    btn.type = "button";
    btn.addEventListener("click", onClick);
    const dot = el("span", "tmap-dot");
    dot.style.setProperty("background", `var(${eraInfo.colorVar})`);
    dot.setAttribute("aria-hidden", "true");
    btn.append(dot);
    const txt = el("span", "tmap-txt");
    txt.append(el("span", "tmap-name", name));
    const se = isOrigin ? null : subtleEra(eraInfo);
    if (se) txt.append(el("span", "tmap-era", se.year ? `${se.label} · ${se.year}` : se.label));
    btn.append(txt);
    if (current) btn.append(el("span", "tmap-here", "you are here"));
    btn.setAttribute("aria-label", `${name}${se ? ", " + se.label + (se.year ? " " + se.year : "") : ""}${current ? ", current" : ""}`);
    if (current) btn.setAttribute("aria-current", "true");
    li.append(btn);
    return li;
  }

  // ---- the mobile journey drawer (a bottom-sheet overlay) ------------------
  let drawerBody: HTMLElement | null = null;
  let drawerScrim: HTMLElement | null = null;
  let drawerRelease: (() => void) | null = null;
  let drawerOpener: HTMLElement | null = null;

  function openJourneyDrawer(): void {
    if (drawerBody) {
      closeJourneyDrawer();
      return;
    }
    // capture the trigger (the Journey button is re-created each renderOrientation)
    // BEFORE closing any sibling drawer, so focus restores to the real opener.
    const trigger = document.activeElement as HTMLElement | null;
    closeExploreDrawer(); // never two drawers / two focus traps at once (no-op if closed)
    closeDeskDrawer(false);
    closeBookmarksDrawer(false);
    closeSavedPathsDrawer(false);
    drawerOpener = trigger;
    const scrim = el("div", "drawer-scrim");
    const sheet = el("div", "journey-drawer");
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-label", "Your trail");
    const grip = el("div", "drawer-grip");
    grip.setAttribute("aria-hidden", "true");
    const closeBtn = button("drawer-close", "Done", closeJourneyDrawer);
    closeBtn.setAttribute("aria-label", "Close your trail");
    const bodyWrap = el("div", "drawer-body");
    bodyWrap.append(buildJourneyPanelInteractive("drawer"));
    sheet.append(grip, closeBtn, bodyWrap);
    scrim.addEventListener("click", closeJourneyDrawer);
    document.body.append(scrim, sheet);
    drawerBody = bodyWrap;
    drawerScrim = scrim;
    syncOverlayBackground(); // inert the app behind the trail drawer
    const handle = trapFocus(sheet, { initialFocus: closeBtn, onEscape: closeJourneyDrawer });
    drawerRelease = () => handle.release();
  }

  function closeJourneyDrawer(): void {
    drawerRelease?.();
    drawerRelease = null;
    drawerScrim?.remove();
    drawerBody?.closest(".journey-drawer")?.remove();
    drawerBody = null;
    drawerScrim = null;
    const opener = drawerOpener;
    drawerOpener = null;
    syncOverlayBackground(); // un-inert the background BEFORE returning focus to it
    opener?.focus?.(); // return focus to the trigger (WCAG 2.4.3)
  }

  // ============================================================================
  // RIGHT-SIDE "EXPLORE NEXT": a read-only projection of the CURRENT source's
  // connected sources (the same honest buckets the inline rabbit-hole used,
  // captured in citeData), plus a "back to your journey" group. Every action
  // delegates to the SAME live DOM mechanism, so DOM-path-as-truth is preserved:
  //   Open    -> clicks the inline card (drills the trail in)
  //   Preview -> the existing Peek sheet (no trail change)
  // ============================================================================

  function toConn(
    it: Item,
    reason: string,
    relOverride?: string,
    strengthOverride?: "strong" | "medium" | "weak",
    anchorLabel?: string,
  ): SourceConnection {
    const rel = getRelationship(it.linkType, it.category);
    const type = getSourceType(it.category);
    return {
      id: it.ref,
      title: itemLabel(it),
      hebrewTitle: it.commentatorHe,
      // The card's context line is, first, a concise description of WHO/WHAT this
      // source is (the "source intelligence" - reused from the curated map). Only
      // when we have no description do we fall back to the structural context
      // line (type + relationship + anchor). This replaces the old per-card
      // "Commentary on <verse>" echo - the verse is already stated once in the head.
      contextLine:
        describeSource(it.commentator, it.ref) ??
        sourceContextLine({
          typeLabel: type.label,
          relationshipLabel: relDisplay(relOverride ?? rel.label),
          anchorLabel: formatContextAnchor(anchorLabel),
        }),
      type: type.label,
      category: it.category,
      colorVar: type.colorVar,
      relationship: relOverride ?? rel.label,
      strength: strengthOverride ?? strengthChip(rel.strength),
      reason,
      onDesk: isOnDesk(it.ref),
      bookmarked: bookmarkedRefs.has(it.ref),
    };
  }

  const exploreActions = {
    // One reading model: "Open" commits the source into the path and reads it in the
    // panel (no separate Preview/peek). Bookmark + Add to Desk stay.
    onContinue: (c: SourceConnection): void => {
      continueByRef(c.id);
    },
    onAddToDesk: (c: SourceConnection, btn: HTMLButtonElement): void => addDeskIdentity(connToDeskItem(c), btn),
    onBookmark: (c: SourceConnection, btn: HTMLButtonElement): void => void bookmarkIdentity(toBookmarkItem(c), btn),
  };

  // Drill the trail into a connected source by clicking its inline card (the real
  // mechanism), un-folding its bundle first if needed. No-op if the card is absent
  // (e.g. beyond the auto-load depth) - we never fabricate a dive.
  function continueByRef(ref: string): boolean {
    if (!currentNode) return false;
    const citelist = currentNode.querySelector(":scope > .sbody > .cites > .citelist");
    const row = citelist?.querySelector(`.srow[data-ref="${attrEsc(ref)}"]`);
    if (!(row instanceof HTMLElement)) return false;
    revealRow(row); // surface through accordion / more-fold / bundle / language filter
    const head = row.querySelector(":scope > .srowhead");
    // Keep the Explore drawer OPEN on mobile: opening a source refreshes the panel to
    // its reader (the open handler runs setTrail -> refreshExplore), so the learner
    // immediately sees what they opened. Closing it here dropped them back to the verse
    // list - it read as "the source didn't open." (No-op difference on desktop, where
    // Explore is a rail.) An already-open row gets no click, so refresh explicitly.
    if (!row.classList.contains("open") && head instanceof HTMLElement) head.click();
    else refreshExplore();
    return true;
  }

  function formatContextAnchor(anchor: string | null | undefined): string | undefined {
    if (!anchor) return undefined;
    return parseCanonicalRef(anchor) ? verseShort(anchor) : anchor;
  }

  function studyEmptyState(title: string, body: string, ghostLabels: string[] = []): HTMLElement {
    const wrap = el("div", "study-empty");
    if (ghostLabels.length) {
      const ghosts = el("div", "study-empty-ghosts");
      ghostLabels.forEach((label) => ghosts.append(el("span", "study-empty-ghost", label)));
      wrap.append(ghosts);
    }
    wrap.append(el("div", "study-empty-title", title), el("div", "study-empty-copy", body));
    return wrap;
  }

  // ---- the trust filter (which trust tiers the Explore panel shows) --------
  // Default = Verified + Probable: the trusted Torah graph. Suggested (thematic
  // / AI / flagged) is opt-in, so exploratory links never sit beside verified
  // ones unasked. State survives re-renders (closure-scoped, not DOM-scoped).
  const exploreTrust = new Set<TrustTier>(["verified", "probable"]);

  function renderTrustFilter(): HTMLElement {
    const row = el("div", "explore-filter");
    row.setAttribute("role", "group");
    row.setAttribute("aria-label", "Filter connections by trust level");
    row.append(el("span", "explore-filter-label", "Show:"));
    const tiers: Array<[TrustTier, string]> = [
      ["verified", "Verified"],
      ["probable", "Probable"],
      ["suggested", "Suggested"],
    ];
    for (const [tier, label] of tiers) {
      const b = el("button", "tfilter-btn", label);
      b.type = "button";
      b.dataset.tier = tier;
      b.setAttribute("aria-pressed", String(exploreTrust.has(tier)));
      b.addEventListener("click", () => {
        if (exploreTrust.has(tier)) exploreTrust.delete(tier);
        else exploreTrust.add(tier);
        // refreshExplore() rebuilds the panel and DESTROYS this button. Focus
        // must move to its rebuilt equivalent in the SAME surface (rail or
        // drawer body) - otherwise focus falls to <body>, and inside the
        // drawer that silently disables the focus trap (Tab containment and
        // Escape both die, since the trap listens on the sheet).
        const host = b.closest(".drawer-body") ?? b.closest(".rail-explore");
        refreshExplore();
        const next = host?.querySelector(`.tfilter-btn[data-tier="${tier}"]`);
        if (next instanceof HTMLElement) next.focus();
      });
      row.append(b);
    }
    return row;
  }

  // ---- "Why this source?" modal: the evidence + provenance behind one card --
  // Everything shown is read off the typed SourceEdge + Evidence the adapter
  // built from real pipeline facts - relationship, evidence sentence, origin,
  // confidence, provider. Suggested/AI connections carry an explicit
  // not-a-verified-citation notice (trust rule).
  function openWhyModal(c: SourceConnection, adapted: AdaptedConnection): void {
    const { edge, evidence } = adapted;
    const badge = getTrustBadge(edge, evidence);
    openModal("Why this source?", () => {
      const box = el("div", "why");

      const src = el("div", "why-source");
      if (c.hebrewTitle) {
        const he = el("span", "why-he", c.hebrewTitle);
        he.lang = "he";
        he.dir = "rtl";
        src.append(he);
      }
      src.append(el("span", "why-en", c.title));
      box.append(src);

      const trust = el("div", "why-trust");
      trust.dataset.tone = badge.tone;
      trust.append(el("span", "trust-chip why-trust-chip", badge.label), el("span", "why-trust-x", badge.explanation));
      const chip = trust.querySelector(".trust-chip") as HTMLElement;
      chip.dataset.tone = badge.tone;
      box.append(trust);

      const row = (k: string, v: string): HTMLElement => {
        const r = el("div", "why-row");
        r.append(el("span", "why-k", k), el("span", "why-v", v));
        return r;
      };
      box.append(row("Relationship", `${c.relationship} (${edge.relationshipType})`));
      box.append(row("Evidence", explainSourceConnection(edge, evidence)));
      box.append(row("Source of connection", originLabel(adapted)));
      box.append(row("Confidence", edge.confidence));
      box.append(row("Provider", edge.provenance.provider ?? "Internal"));
      if (badge.tone === "suggested") {
        box.append(el("p", "why-note", "Important: this is a suggestion, not a verified citation."));
      }
      return box;
    });
  }

  // The per-bucket honesty vocabulary, stated ONCE: the reason line and the
  // direction-honest relationship/strength overrides every Explore surface
  // (the category accordions) must agree on. This is
  // ACCURACY-LOCK wording - "cited here" is earned by the dual gate;
  // connected (non-"on"-shaped) refs anchored on a SOURCE can invert
  // direction, so they get the neutral "cross-reference" + softened strength,
  // never an asserted backwards citation.
  const BUCKET_META: Record<ConnectionBucket, { reason: string; rel?: string; strength?: "strong" | "medium" | "weak" }> = {
    cited: { reason: "Cited in this source", rel: "cited here" },
    commentaryOn: { reason: "Comments on this source" },
    otherConnected: { reason: "Related to this source", rel: "cross-reference", strength: "weak" },
  };

  // Build one group of explore cards, BACKED BY TYPED EDGES: each card
  // carries the SourceEdge + Evidence the adapter derived for its bucket. The
  // adapter + session recording run in DEV builds and under isSourceDebug() (the
  // integrity audit's only readers); the trust VOCABULARY - badge, tier filter,
  // Why? action - renders only under isSourceDebug() (learners see sources as
  // sources).
  // Filtered-out cards are counted, never silently dropped. Multiple comments
  // from one commentator still collapse to a single representative card.
  function buildExploreGroup(
    items: Item[],
    bucket: ConnectionBucket,
  ): { nodes: HTMLElement[]; shown: number; hidden: number; langHidden: number; byStat: Map<string, number> } {
    const byStat = new Map<string, number>();
    const currentRef = currentNode?.dataset.ref ?? "";
    if (!items.length || !currentRef) return { nodes: [], shown: 0, hidden: 0, langHidden: 0, byStat };
    const meta = BUCKET_META[bucket];
    const debug = isSourceDebug();
    const mode = getSourceLangMode();
    const wrap = el("div", "explore-cards");
    let shown = 0;
    let hidden = 0;
    let langHidden = 0;
    for (const g of bundleByCommentator(items)) {
      const rep = g.items[0];
      // The typed edge + session record are read ONLY by the DEV integrity hook
      // (app.ts __sourceIntegrity) and the debug trust vocabulary, so a production
      // learner (debug off) pays for none of it. The badge is debug-only.
      const adapted = debug || import.meta.env.DEV ? edgeFromBucket(bucket, currentRef, rep) : null;
      if (adapted) recordAdapted(adapted); // session graph store (audited by the DEV integrity hook)
      const badge = debug && adapted ? getTrustBadge(adapted.edge, adapted.evidence) : null;
      // The tier gate is a debug inspection tool. Skipping it in normal mode is
      // behavior-neutral on live data (the adapter only emits verified/high
      // edges) and guarantees learners never lose sources to an invisible filter.
      if (badge && !exploreTrust.has(tierForTone(badge.tone))) {
        hidden += 1;
        continue;
      }
      // The "Show sources" language filter: the card shows if ANY member is
      // readable in the active mode (one commentator per bundle - they nearly
      // always agree). Hidden cards are counted into the panel's calm note.
      if (!g.items.some((it) => shouldShowSourceForDisplayMode(it, mode))) {
        langHidden += 1;
        continue;
      }
      const conn = toConn(rep, meta.reason, meta.rel, meta.strength, currentNode?.dataset.label ?? currentRef);
      // "Where else this comes up" is the catch-all of mixed kinds - name each
      // card's kind (its real Sefaria category) so the learner sees Talmud vs
      // midrash vs parallel verse at a glance. The other two buckets are already
      // self-describing by their title, so they stay calm (no kind tag).
      if (bucket === "otherConnected") conn.kind = getSourceType(rep.category).label;
      if (badge) conn.trust = { label: badge.label, tone: badge.tone };
      if (g.items.length > 1) {
        conn.title = `${conn.title} (${g.items.length})`;
        conn.reason = `${meta.reason} - ${g.items.length} comments`;
      }
      const acts = adapted && badge ? { ...exploreActions, onWhy: () => openWhyModal(conn, adapted) } : exploreActions;
      const card = renderSourceCard(conn, acts, { debug });
      // Stat tally + stamp live in the SAME loop that renders the card, so the
      // stats box can never drift from what the accordions actually hold.
      const statKey = statKeyFor(rep);
      card.dataset.stat = statKey;
      byStat.set(statKey, (byStat.get(statKey) ?? 0) + 1);
      wrap.append(card);
      shown += 1;
    }
    if (!shown) return { nodes: [], shown: 0, hidden, langHidden, byStat };
    return { nodes: [wrap], shown, hidden, langHidden, byStat };
  }

  // Explore-panel ACCORDION: one category group behind a collapsed header.
  // xcards are pure projections (never .srow), so plain [hidden] is safe here -
  // no fold-channel ceremony, and no replay involvement. Toggling flips the DOM
  // in place (never refreshExplore) so the focused header survives. The choice
  // is remembered in the SAME sectionMemory the center-column disclosures use
  // (one session-memory mechanism, one lifetime), under an `explore:` prefix
  // that cannot collide with the verse/bucket key families.
  function exploreAccordion(
    label: string,
    key: string,
    group: { nodes: HTMLElement[]; shown: number },
    opts: { defaultOpen?: boolean; memoryKey?: string; dotVar?: string } = {},
  ): HTMLElement[] {
    if (!group.shown) return [];
    const memKey = opts.memoryKey ?? `explore:${currentNode?.dataset.ref ?? ""}::${key}`;
    const open = sectionMemory.get(memKey) ?? !!opts.defaultOpen;
    // Verse-mode sections carry their type ink (dotVar) so the section head doubles
    // as the color legend the old static stats wall used to be - dot ALWAYS paired
    // with the text label, never color alone.
    const head = el("button", opts.dotVar ? "explore-acc-btn explore-acc-btn-rich" : "explore-acc-btn");
    head.type = "button";
    head.setAttribute("aria-expanded", String(open));
    if (opts.dotVar) {
      const dot = el("span", "explore-acc-dot");
      dot.style.setProperty("--dot", `var(${opts.dotVar})`);
      dot.setAttribute("aria-hidden", "true");
      head.append(dot);
    }
    head.append(
      el("span", "explore-acc-label", label),
      el("span", "explore-acc-n", String(group.shown)),
      el("span", "scaret explore-acc-caret"),
    );
    const body = el("div", "explore-acc-body");
    body.append(...group.nodes);
    body.hidden = !open;
    head.addEventListener("click", () => {
      const now = head.getAttribute("aria-expanded") !== "true";
      head.setAttribute("aria-expanded", String(now));
      body.hidden = !now;
      sectionMemory.set(memKey, now);
    });
    return [head, body];
  }

  // The open source's TEXT, read inside the wide Explore panel (the center accordion
  // is the hidden trail engine). Re-rendered from getText (memoized) on every panel
  // rebuild; mirrors the reader's Translation toggle + font scale since the panel sits
  // outside .reader and would not otherwise inherit them.
  function buildSourceReader(node: HTMLElement): HTMLElement {
    const ref = node.dataset.ref ?? "";
    const readerEl = node.closest<HTMLElement>(".reader");
    const wrap = el("div", "explore-reader");
    if (readerEl?.classList.contains("hide-trans")) wrap.classList.add("hide-trans");
    const scale = readerEl?.style.getPropertyValue("--reader-scale");
    if (scale) wrap.style.setProperty("--reader-scale", scale);
    wrap.append(loadingRow());
    const fill = (): void => {
      wrap.setAttribute("aria-busy", "true"); // AT: this panel is loading (cleared on settle)
      void getText(ref)
        .then((t) => {
          if (!wrap.isConnected) return;
          wrap.removeAttribute("aria-busy");
          wrap.replaceChildren(textBlock(t.he, t.en), sattribEl(t));
          // Project the engine's in-text citation pills onto the panel text:
          // anchored refs (wireCiteAnchors) + free-text find-refs, BOTH routing
          // clicks through the hidden engine row (`node`) so trail/history/replay
          // stay byte-identical. Links come from the already-loaded buckets (no
          // second fetch); find-refs reuses the engine's per-source cache (no
          // budget double-spend). Skipped until the cites land - the panel
          // rebuilds when they do.
          const d = citeData.get(node);
          if (d) {
            const items = [...d.cited, ...d.commentaryOn, ...d.otherConnected];
            wireCiteAnchors(wrap.querySelectorAll<HTMLAnchorElement>(".stext a"), items, node);
            scheduleSenEnhancementOn(wrap.querySelector<HTMLElement>(".stext > .sen"), ref, items, node);
          }
        })
        .catch((err) => {
          if (!wrap.isConnected) return;
          wrap.removeAttribute("aria-busy");
          wrap.replaceChildren(errorRow(err, fill));
        });
    };
    fill();
    return wrap;
  }

  // An open source with NO catalogued onward links is a genuine leaf of the
  // trail (Sefaria simply has no links indexed for it). Say so plainly and hand
  // the learner the real next move - back to the pasuk's full source list, where
  // the dozens of things they CAN open live. Replaces the old flat "deep in the
  // rabbit hole" copy, which read as "something's broken / missing".
  function leafState(label: string): HTMLElement {
    const box = el("div", "explore-leaf");
    box.append(el("div", "explore-leaf-title", "Nothing to open from here"));
    const { verse } = trailChain(currentNode);
    const vref = verse?.dataset.ref ?? "";
    box.append(
      el(
        "div",
        "explore-leaf-copy",
        vref
          ? `${label} has no catalogued sources to follow. Step back to ${verseShort(vref)} to keep exploring its other sources.`
          : `${label} has no catalogued sources to follow.`,
      ),
    );
    if (verse instanceof HTMLElement && vref) {
      box.append(
        button("explore-leaf-btn", `Back to all sources on ${verseShort(vref)}`, () => {
          collapseToVerse(verse);
          selectVerse(verse, vref);
        }),
      );
    }
    return box;
  }

  function buildExplorePanel(): HTMLElement {
    if (selectedVerseRef && !(selectedVerseEl instanceof HTMLElement && selectedVerseEl.isConnected)) {
      selectedVerseRef = null;
      selectedVerseEl = null;
    }
    const panel = el("div", "explore-panel");
    const head = el("div", "explore-head");
    head.append(el("div", "explore-title", "Explore next"));

    if (selectedVerseRef && selectedVerseEl) {
      // Make the SELECTED PASUK prominent (it is what the panel is about): the full
      // reference, then a short snippet of its own text so the learner always knows
      // which verse these sources hang off.
      const vid = el("div", "explore-vid");
      vid.append(el("div", "explore-vref", selectedVerseRef));
      const snip = verseSnippet(selectedVerseEl);
      if (snip.text) {
        const t = el("div", "explore-vtext", snip.text);
        t.lang = snip.lang;
        if (snip.lang === "he") t.dir = "rtl";
        vid.append(t);
      }
      head.append(vid);
      panel.classList.add("explore-panel-verse");
      panel.append(head);

      const links = verseScent.get(selectedVerseRef);
      const mode = getSourceLangMode();
      const visible = links?.length ? scentVisible(links, mode) : [];
      const loaded = (() => {
        const ch = verseChapter(selectedVerseRef);
        return ch !== null && scentChaptersLoaded.has(ch);
      })();

      if (visible.length) {
        // The at-a-glance hero (kinds / commentaries / total), then the note, then
        // the per-category accordions it summarizes.
        panel.append(buildVerseHero(visible));
        panel.append(el("div", "explore-note", "Open any source to read it here and follow where it leads."));
        panel.append(...buildVerseExploreSections(selectedVerseRef, visible));
      } else if (mode !== "default" && (links?.length ?? 0) > 0) {
        const word = mode === "hebrew" ? "Hebrew" : "English";
        panel.append(el("div", "explore-empty", `No ${word} sources are available for this verse.`));
      } else if (terrainFailed) {
        panel.append(el("div", "explore-empty", "Source availability couldn't be loaded right now."));
      } else if (!loaded) {
        panel.append(el("div", "explore-loading", "Scanning sources for this verse..."));
      } else {
        panel.append(el("div", "explore-empty", "No accepted sources found here yet."));
      }

      const hiddenN = links ? links.length - visible.length : 0;
      if (mode !== "default" && hiddenN > 0) {
        const word = mode === "hebrew" ? "Hebrew" : "English";
        panel.append(
          langNote(
            `${word} only: ${hiddenN} ${hiddenN === 1 ? "source is" : "sources are"} hidden because no ${word} text is available.`,
            () => applySourceLang("default"),
            "explore-langnote",
          ),
        );
      }

      return panel;
    }

    if (!currentNode) {
      head.append(el("div", "explore-sub", "Open a source to see where you can go."));
      panel.append(head);
      panel.append(studyEmptyState("Choose a place to begin", "Select a pasuk or open a source to see what connects next.", ["Pasuk", "Sources"]));
      return panel;
    }

    // The reading head carries the depth chip ("N deep") so the learner always knows
    // how far down the rabbit-hole this source sits - right where they read it.
    const depthN = trailChain(currentNode).rows.length;
    const readingRow = el("div", "explore-sub explore-reading-row");
    readingRow.append(el("span", "explore-reading-label", `Reading ${currentNode.dataset.label ?? "this source"}`));
    if (depthN > 0) readingRow.append(el("span", "explore-depth", `${depthN} deep`));
    head.append(readingRow);
    panel.append(head);
    // Read the open source IN the panel (the center accordion is the hidden engine).
    panel.classList.add("explore-panel-reading");
    panel.append(buildSourceReader(currentNode));

    const data = citeData.get(currentNode);
    let any = false;
    if (data) {
      const total = data.cited.length + data.commentaryOn.length + data.otherConnected.length;
      if (!total) {
        // No buckets yet: still recovering verbatim quotes -> "finding..."; only a
        // settled empty (recovery done / never ran) is the honest leaf.
        panel.append(
          isRecovering(currentNode)
            ? el("div", "explore-loading", "Finding the sources this text quotes...")
            : leafState(currentNode.dataset.label ?? "This source"),
        );
        any = true;
      } else {
        // Each bucket becomes typed, evidence-backed edges (sourceGraphAdapter).
        // In DEBUG, the trust machinery is visible: the tier filter, the trust
        // SECTION headings (GROUP_META - shared with the query layer so wording
        // cannot drift), and the hidden-by-filter count. Learners get only the
        // honest group labels - the trust layer keeps running invisibly.
        const debug = isSourceDebug();
        if (debug) panel.append(renderTrustFilter());

        // Per-bucket reasons + direction-honest overrides come from BUCKET_META
        // (one source of truth for every explore card).
        const cited = buildExploreGroup(data.cited, "cited");
        const commentary = buildExploreGroup(data.commentaryOn, "commentaryOn");
        const connected = buildExploreGroup(data.otherConnected, "otherConnected");

        // No "What connects here" summary box: it duplicated the bucket counts below
        // and read as a confusing fourth category. The labelled buckets ARE the map.
        const group = (label: string, key: string, g: { nodes: HTMLElement[]; shown: number }): HTMLElement[] =>
          debug
            ? g.shown
              ? [el("div", "explore-group-label", label), ...g.nodes]
              : []
            : exploreAccordion(label, key, g);

        // Lead-in above the trail doors so the reader knows these prominent
        // buckets are the onward moves. One per panel, learner mode only.
        if (!debug && cited.shown + commentary.shown + connected.shown > 0) {
          panel.append(el("div", "explore-trail-lead", "Follow the trail"));
        }

        if (cited.shown + commentary.shown > 0) {
          if (debug) panel.append(el("div", "explore-section", GROUP_META.verified.title));
          panel.append(...group("What this text cites", "cited", cited));
          // The genuine in-text citation is the prize - say plainly when there is none.
          if (!data.cited.length) {
            panel.append(el("div", "explore-note", "No in-text citations found in this source."));
          }
          panel.append(...group("Commentaries on this", "commentary", commentary));
          any = true;
        } else if (!data.cited.length) {
          panel.append(el("div", "explore-note", "No in-text citations found in this source."));
          any = true; // the note IS content - never co-render the fallback empty-state under it
        }
        if (connected.shown > 0) {
          if (debug) panel.append(el("div", "explore-section explore-section-rel", GROUP_META.related.title));
          panel.append(...group("Where else this comes up", "connected", connected));
          any = true;
        }
        const hiddenN = cited.hidden + commentary.hidden + connected.hidden;
        if (hiddenN > 0) {
          // Only reachable in debug (the tier gate is debug-only above).
          panel.append(
            el("div", "explore-hidden", `${hiddenN} connection${hiddenN === 1 ? "" : "s"} hidden by the trust filter`),
          );
          any = true;
        }
        // The language filter's calm transparency line (one per panel) + the
        // Show-all escape hatch back to Default.
        const langN = cited.langHidden + commentary.langHidden + connected.langHidden;
        if (langN > 0) {
          const mode = getSourceLangMode();
          const word = mode === "hebrew" ? "Hebrew" : "English";
          panel.append(
            langNote(
              `${word} only: ${langN} ${langN === 1 ? "connection" : "connections"} hidden because no ${word} text is available.`,
              () => applySourceLang("default"),
              "explore-langnote",
            ),
          );
          any = true;
        }
      }
    } else if (isRecovering(currentNode) || currentNode.querySelector(":scope > .sbody > .cites [role='status']")) {
      panel.append(el("div", "explore-loading", "Finding where this source can take you..."));
      any = true; // a loader is in flight - never co-render the leaf fallback beneath it
    }

    // (No "Back to your path" projection here: the header Back/Forward + the LEFT
    // journey rail are the way back. An in-panel back-list just made it easy to
    // jump out and lose your place, so it was removed.)

    if (!any) {
      // Defensive: citeData not yet recorded for this node (a race), with no
      // in-flight loader either. Treat it as a leaf rather than a flat dead-end.
      panel.append(
        isRecovering(currentNode)
          ? el("div", "explore-loading", "Finding the sources this text quotes...")
          : leafState(currentNode.dataset.label ?? "This source"),
      );
    }
    return panel;
  }

  // Re-render Explore into the desktop right rail (if present) AND the open drawer.
  // A rebuild that swallows the focused element drops focus to <body>; inside the
  // drawer that silently KILLS the focus trap (trapFocus listens on the sheet, so
  // Tab containment and Escape both die). So: if the rebuild is about to destroy
  // the active element, land focus on a stable survivor - the sheet's Done button,
  // or the rail's first control. Callers that re-focus a rebuilt equivalent (the
  // trust filter) run after this and simply override it.
  function refreshExplore(): void {
    if (chromeWalk > 0) {
      exploreDirty = true;
      return;
    }
    const active = document.activeElement;
    const rail = document.querySelector(".rail-explore");
    const railHadFocus = rail instanceof HTMLElement && active instanceof HTMLElement && rail.contains(active);
    const drawerHadFocus = !!exploreDrawerBody && active instanceof HTMLElement && exploreDrawerBody.contains(active);
    // Preserve scroll on an INCIDENTAL rebuild (bookmark/desk/filter), but reset to
    // the top when the source/verse changes (you read the newly-opened one from its
    // start). The reader text makes the panel tall, so a stray scroll jump is jarring.
    const placeKey = currentNode?.dataset.ref ?? selectedVerseRef ?? "";
    const samePlace = placeKey === lastExplorePlaceKey;
    lastExplorePlaceKey = placeKey;
    const drawerScrollEl = exploreDrawerBody?.closest<HTMLElement>(".drawer-body") ?? exploreDrawerBody;
    const railTop = rail instanceof HTMLElement ? rail.scrollTop : 0;
    const drawerTop = drawerScrollEl instanceof HTMLElement ? drawerScrollEl.scrollTop : 0;
    if (rail) rail.replaceChildren(buildExplorePanel());
    if (exploreDrawerBody) exploreDrawerBody.replaceChildren(buildExplorePanel());
    if (rail instanceof HTMLElement) rail.scrollTop = samePlace ? railTop : 0;
    if (drawerScrollEl instanceof HTMLElement) drawerScrollEl.scrollTop = samePlace ? drawerTop : 0;
    if (drawerHadFocus) {
      const close = exploreDrawerBody?.closest(".journey-drawer")?.querySelector(".drawer-close");
      if (close instanceof HTMLElement) close.focus();
    } else if (railHadFocus && rail instanceof HTMLElement) {
      const first = rail.querySelector("button");
      if (first instanceof HTMLElement) first.focus();
    }
  }

  // ---- the mobile/narrow Explore drawer (mirrors the journey drawer) --------
  let exploreDrawerBody: HTMLElement | null = null;
  let exploreScrim: HTMLElement | null = null;
  let exploreRelease: (() => void) | null = null;
  let exploreOpener: HTMLElement | null = null;

  function openExploreDrawer(): void {
    if (exploreDrawerBody) {
      closeExploreDrawer();
      return;
    }
    const trigger = document.activeElement as HTMLElement | null;
    closeJourneyDrawer(); // never two drawers / two focus traps at once (no-op if closed)
    closeDeskDrawer(false);
    closeBookmarksDrawer(false);
    closeSavedPathsDrawer(false);
    exploreOpener = trigger;
    const scrim = el("div", "drawer-scrim");
    const sheet = el("div", "journey-drawer explore-drawer");
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-label", "Explore next");
    const grip = el("div", "drawer-grip");
    grip.setAttribute("aria-hidden", "true");
    const closeBtn = button("drawer-close", "Done", closeExploreDrawer);
    closeBtn.setAttribute("aria-label", "Close Explore next");
    const bodyWrap = el("div", "drawer-body");
    bodyWrap.append(buildExplorePanel());
    // Mobile: pin the full SELECTED VERSE at the top so the learner never loses the
    // pasuk these sources hang off (the center is hidden behind the sheet). Absent
    // when reading deep in a source (no verse selected) - the source reader leads.
    const verseHead = buildDeskVerseHead();
    if (verseHead) {
      verseHead.classList.add("explore-drawer-verse");
      sheet.append(grip, closeBtn, verseHead, bodyWrap);
    } else {
      sheet.append(grip, closeBtn, bodyWrap);
    }
    scrim.addEventListener("click", closeExploreDrawer);
    document.body.append(scrim, sheet);
    exploreDrawerBody = bodyWrap;
    exploreScrim = scrim;
    syncOverlayBackground(); // inert the app behind the explore drawer
    const handle = trapFocus(sheet, { initialFocus: closeBtn, onEscape: closeExploreDrawer });
    exploreRelease = () => handle.release();
  }

  function closeExploreDrawer(): void {
    exploreRelease?.();
    exploreRelease = null;
    exploreScrim?.remove();
    exploreDrawerBody?.closest(".explore-drawer")?.remove();
    exploreDrawerBody = null;
    exploreScrim = null;
    syncOverlayBackground(); // un-inert the background BEFORE returning focus to it
    const opener = exploreOpener;
    exploreOpener = null;
    opener?.focus?.();
  }

  // ---- mobile header actions: Bookmarks + Saved Paths -----------------------
  // Mobile-only (CSS hides .headeracts at >=960px, where the orientation bar
  // carries these). Filled from renderBottomNav's choke point so the Bookmarks
  // count stays live. Compact icon pills in the header "?" family; the count
  // badge pops once, on the 0 -> N transition.
  function renderHeaderActions(): void {
    const host = document.querySelector(".headeracts");
    if (!host) return;
    const justAppeared = prevHeaderBookmarkCount === 0 && bookmarkCount > 0;
    prevHeaderBookmarkCount = bookmarkCount;
    // title = a plain hover tooltip so the icon's meaning is discoverable.
    const pill = (glyph: string, aria: string, title: string, onClick: () => void): HTMLButtonElement => {
      const b = el("button", "headeract") as HTMLButtonElement;
      b.type = "button";
      b.setAttribute("aria-label", aria);
      b.title = title;
      const ic = el("span", `hi ${glyph}`);
      ic.setAttribute("aria-hidden", "true");
      b.append(ic);
      b.addEventListener("click", onClick);
      return b;
    };
    const acts: HTMLElement[] = [];
    // Bookmarks: ALWAYS shown (the drawer carries its own "No bookmarks yet" empty
    // state); the count badge still appears only when there is something saved.
    const bm = pill(
      "hi-bookmark",
      bookmarkCount ? `Open Bookmarks, ${bookmarkCount} saved` : "Open Bookmarks",
      bookmarkCount ? `Bookmarks (${bookmarkCount})` : "Bookmarks",
      openBookmarksSurface,
    );
    if (bookmarkCount) {
      const n = el("span", `hi-count${justAppeared ? " hi-count-in" : ""}`, bookmarkCount > 9 ? "9+" : String(bookmarkCount));
      n.setAttribute("aria-hidden", "true");
      bm.append(n);
    }
    acts.push(bm);
    // Saved Trails: ALWAYS shown too, so a returning learner with persisted trails can
    // reach them on a fresh load (was gated on a LIVE session trail - so saved trails
    // were unreachable until you started a new one; the drawer has its own empty state).
    acts.push(pill("hi-paths", "Open Saved Trails", "Saved trails", openSavedPathsSurface));
    host.replaceChildren(...acts);
  }

  // ---- mobile bottom nav: Trail / Explore / Desk / Start ------------
  function renderBottomNav(): void {
    renderHeaderActions(); // header owns Bookmarks/Saved Paths on mobile now
    const host = document.querySelector(".botnav-host");
    if (!host) return;
    const depth = trailChain(currentNode).rows.length;
    const trailN = sessionTrail.length;
    const deskN = getDeskItems().length;
    if (!trailN && !depth && !deskN && !selectedVerseRef) {
      host.replaceChildren(); // no journey yet -> nothing to navigate
      return;
    }
    const nav = el("nav", "botnav");
    nav.setAttribute("aria-label", "Journey navigation");
    const mk = (icon: string, label: string, aria: string, onClick: () => void): HTMLElement => {
      const b = el("button", "botnav-btn");
      b.type = "button";
      b.setAttribute("aria-label", aria);
      b.append(el("span", `botnav-i botnav-i-${icon}`, ""), el("span", "botnav-l", label));
      b.firstElementChild?.setAttribute("aria-hidden", "true");
      b.addEventListener("click", onClick);
      return b;
    };
    if (trailN) nav.append(mk("trail", "Trail", "Open your trail", openJourneyDrawer));
    if (depth || selectedVerseRef) nav.append(mk("explore", "Explore", "Open where you can go next", openExploreDrawer));
    if (deskN) nav.append(mk("desk", `Desk (${deskN})`, `Open Source Desk - ${deskN} open`, openDeskSurface));
    if (depth) nav.append(mk("home", "Start", "Start - return to the original source", returnToStart));
    host.replaceChildren(nav);
  }

  // ---- deep-link replay + warm history restore ------------------------------
  // The shared walk: open the route's chain through the REAL click handlers,
  // one level at a time, awaiting loads. Idempotent by construction - already-
  // open rows are skipped, cached fetches resolve instantly - so a no-op apply
  // is cheap and a half-open DOM (cancelled walk) is a valid starting state.
  async function walkPath(path: string[], cancelled?: () => boolean): Promise<void> {
    const [verseRef, ...srcRefs] = path;
    const verseEl = document.querySelector(`.verse[data-ref="${attrEsc(verseRef)}"]`);
    if (!(verseEl instanceof HTMLElement)) return;
    const srcBtn = verseEl.querySelector(":scope > .vactions > .srcbtn");
    const panel = verseEl.querySelector(":scope > .sources");
    if (!(panel instanceof HTMLElement) || !(srcBtn instanceof HTMLElement)) return;
    if (!verseEl.classList.contains("show-sources")) srcBtn.click();
    await waitFor(() => !!panel.querySelector(":scope > .srow, .snote, .srowerr"));
    if (cancelled?.()) return;

    let scope: HTMLElement = panel;
    for (let i = 0; i < srcRefs.length; i++) {
      const row = scope.querySelector(`:scope > .srow[data-ref="${attrEsc(srcRefs[i])}"]`);
      if (!(row instanceof HTMLElement)) break;
      revealRow(row); // surface through accordion / more-fold / bundle / language filter
      const head = row.querySelector(":scope > .srowhead");
      if (!row.classList.contains("open") && head instanceof HTMLElement) head.click();
      const body = row.querySelector(":scope > .sbody");
      if (!(body instanceof HTMLElement)) break;
      await waitFor(() => !!body.querySelector(".stext, .srowerr"));
      if (cancelled?.()) return;
      if (i === srcRefs.length - 1) break;
      await waitFor(() => !!body.querySelector(".cites .citelist"), CITELIST_WAIT_MS);
      if (cancelled?.()) return;
      const citelist = body.querySelector(".cites .citelist");
      if (!(citelist instanceof HTMLElement)) break;
      scope = citelist;
    }
  }

  async function replayPath(path: string[], opts: ReplayOpts = {}): Promise<void> {
    if (path.length < 2) return;
    const cancel = beginWalk(opts.cancelled); // latest wins: a newer walk or mount cancels this one
    chromeWalk++;
    beginRestore();
    try {
      await walkPath(path, cancel);
    } finally {
      endRestore();
      chromeWalk--;
      if (chromeWalk === 0) flushChrome(); // ONE rail rebuild for the whole walk
      // One entry for the whole user action (side-path jump), pushed for the
      // ACHIEVED chain - never for a route the walk could not complete.
      if (opts.push && !cancel()) {
        navHistory.pushRoute(buildRoute(parashaRef, chainRefs()));
      }
    }
  }

  // Close the open rows that diverge from the target route (the warm-restore
  // pruning half). Runs inside a restore bracket - its setTrail never pushes.
  function closeDivergent(target: string[]): void {
    const cur = chainRefs();
    if (!cur.length) return;
    if (cur[0] !== target[0]) {
      // different verse: close the whole current chain (the panel stays open)
      const { verse } = trailChain(currentNode);
      if (verse) {
        collapseWithin(verse);
        setTrail(null);
      }
      return;
    }
    let i = 1;
    while (i < cur.length && i < target.length && cur[i] === target[i]) i++;
    if (i >= cur.length) return; // current chain is a prefix of the target
    const { rows } = trailChain(currentNode); // rows[k] <-> cur[k+1]
    const divergent = rows[i - 1];
    if (divergent) {
      const host = divergent.parentElement;
      closeRowTree(divergent);
      // closed rows become language-filterable again - keep counts honest
      if (host instanceof HTMLElement) annotateLang(host);
      setTrail(i - 2 >= 0 ? rows[i - 2] : null);
    }
  }

  async function applyRoute(target: string[], opts: ApplyOpts = {}): Promise<void> {
    const cancel = beginWalk(opts.cancelled); // latest wins across walks AND mounts
    const beforeKey = chainRefs().join("~");
    chromeWalk++;
    beginRestore();
    try {
      if (target.length < 2) {
        // parasha-only route (or a bare verse): the journey state is "no rows
        // open". Close the CHAIN only - source panels are browse state, not
        // chain state, so Back never undoes a panel-open the user still wants
        // (one Back must not feel like two undos).
        if (document.querySelector(".srow.open")) {
          collapseWithin(document);
          setTrail(null);
        }
        return;
      }
      // chunked verse fill: the target verse may not be streamed in yet
      await waitFor(() => !!document.querySelector(`.verse[data-ref="${attrEsc(target[0])}"]`), 3000);
      if (cancel()) return;
      closeDivergent(target);
      await walkPath(target, cancel);
    } finally {
      endRestore();
      chromeWalk--;
      if (chromeWalk === 0) flushChrome(); // ONE rail rebuild for the whole walk
      if (!cancel()) {
        // Reconcile policy: a SHORTFALL (slow load or a card that vanished
        // upstream) leaves the entry's URL as the INTENT - traversing to it
        // again simply retries the walk; rewriting it would permanently gut a
        // journey state over a transient stall. A DIVERGENCE (a real user
        // click landed mid-walk) earns its own entry instead.
        const achievedPath = chainRefs();
        const diverged = achievedPath.some((seg, i) => target[i] !== seg);
        if (diverged) navHistory.pushRoute(buildRoute(parashaRef, achievedPath));
        // Announce only real landings: a changed chain, or an actual
        // traversal (direction known). The popstate/hashchange echo and other
        // no-op re-applies stay silent.
        const changed = achievedPath.join("~") !== beforeKey;
        if (changed || opts.direction) {
          const depth = trailChain(currentNode).rows.length;
          const word =
            opts.direction === "forward" ? "Went forward" : opts.direction === "back" ? "Went back" : "Returned";
          announce(
            depth
              ? `${word} - ${currentNode?.dataset.label ?? "source"}, ${depth} ${depth === 1 ? "step" : "steps"} deep.`
              : `${word} - back to the pasuk.`,
          );
        }
      }
    }
  }

  // initial paint of any mounted rails + bottom nav once the verses exist
  if (isDesktop() || document.querySelector(".rail-journey, .rail-explore")) {
    refreshJourney();
    refreshExplore();
  }
  renderBottomNav();
  // kick off the idle-time scent layer once the shell is painted (it waits for
  // idle/fallback-timer before its FIRST request - never delays verse paint)
  startTerrain();

  return { orientationBar, currentSourceCard, statline, srStatus, parashaRef, attachVerse, replayPath, applyRoute, applySourceLang, refreshDisplay: refreshExplore };
}

// Clamp a label for use inside a button ("Back to <label>"), keeping it scannable.
function truncLabel(s: string, n = 18): string {
  return s.length > n ? s.slice(0, n - 1).trimEnd() + "..." : s;
}

// ---- pure helpers ------------------------------------------------------------

// Group the accepted links by category (taxonomy allowlist + order), de-duping to
// one row per commentator/verse-ref, ordered within a category by connection
// strength (direct before related before thematic), then name. Out-of-taxonomy
// sources are dropped here. Threads linkType + category onto each Item so the row
// dataset (and thus the deep-link replay) keeps the honest relationship.
export function groupLinks(links: SourceLink[]): Array<{ category: string; items: Item[] }> {
  // Per-segment availability merges into the ONE deduped Item via the shared
  // mergeAvail rule (the scent layer's parity dedupe uses the same function):
  // opening the stripped ref shows the whole span, so ANY English segment
  // means English is really there.
  const byRef = new Map<string, Item>();
  const byCat = new Map<string, Item[]>();
  for (const l of links) {
    if (!acceptSourceLink(l)) continue;
    const ref = commentatorVerseRef(l);
    const prior = byRef.get(ref);
    if (prior) {
      prior.hasEn = mergeAvail(prior.hasEn, l.hasEn);
      prior.hasHe = mergeAvail(prior.hasHe, l.hasHe);
      prior.compDate ??= l.compDate; // any non-null segment is authoritative (same work)
      prior.era ??= l.era;
      continue;
    }
    const item: Item = {
      commentator: l.commentator,
      ref,
      commentatorHe: l.commentatorHe,
      linkType: l.linkType,
      category: l.category,
      hasEn: l.hasEn,
      hasHe: l.hasHe,
      compDate: l.compDate,
      era: l.era,
    };
    byRef.set(ref, item);
    const arr = byCat.get(l.category) ?? [];
    arr.push(item);
    byCat.set(l.category, arr);
  }
  for (const items of byCat.values()) {
    items.sort(
      (a, b) =>
        strengthRank(getRelationship(a.linkType, a.category).strength) -
          strengthRank(getRelationship(b.linkType, b.category).strength) ||
        // The most-studied meforshim lead their section (Rashi before
        // Abarbanel) - prominence.ts; unlisted names stay alphabetical after.
        prominenceRank(a.commentator, a.ref) - prominenceRank(b.commentator, b.ref) ||
        a.commentator.localeCompare(b.commentator),
    );
  }
  return [...byCat.keys()]
    .sort((a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b))
    .map((category) => ({ category, items: byCat.get(category)! }));
}

// Split the verse-level Commentary list into ERA sections (classic / later /
// more), per torah-map's curated rishonim/acharonim split (era.ts). Items keep
// their incoming order inside a section; empty sections are dropped; anything
// torah-map does not classify lands honestly in "More commentaries".
export interface CommentarySection {
  key: "classic" | "later" | "more";
  label: string;
  items: Item[];
}
export function sectionizeCommentary(items: Item[]): CommentarySection[] {
  const sections: CommentarySection[] = [
    { key: "classic", label: "Classic commentaries", items: [] },
    { key: "later", label: "Later commentaries", items: [] },
    { key: "more", label: "More commentaries", items: [] },
  ];
  for (const it of items) {
    const era = commentaryEra(it.commentator, it.ref);
    sections[era === "classic" ? 0 : era === "later" ? 1 : 2].items.push(it);
  }
  return sections.filter((s) => s.items.length > 0);
}

// Section verse-level links into labelled groups, shared by the two callers that need
// it (the verse Explore accordions and the in-place source list). The rule: the broad
// "Commentary" category splits into torah-map era tiers (sectionizeCommentary); every
// other category presents under its honest TYPE label, and sibling categories that
// share a type label (Kabbalah/Chasidut/Musar -> "Jewish Thought"; Mishnah/Talmud/
// Tosefta -> "Talmud") merge into ONE section, in first-seen order. Each caller maps the
// result into its own DOM, reading only the fields it needs: `key` for accordion memory,
// `colorVar` + `commentaryKey` for the source list's reason line.
export interface SectionGroup {
  key: string; // stable id: "commentary:<era>" or "type:<label>"
  label: string;
  colorVar: string;
  items: Item[];
  commentaryKey?: CommentarySection["key"]; // set only for commentary era sections
}
export function sectionizeGroups(groups: Array<{ category: string; items: Item[] }>): SectionGroup[] {
  const secs: SectionGroup[] = [];
  const byLabel = new Map<string, SectionGroup>();
  for (const g of groups) {
    const type = getSourceType(g.category);
    if (g.category.trim().toLowerCase() === "commentary") {
      for (const sec of sectionizeCommentary(g.items)) {
        secs.push({ key: `commentary:${sec.key}`, label: sec.label, colorVar: type.colorVar, items: sec.items, commentaryKey: sec.key });
      }
      continue;
    }
    const existing = byLabel.get(type.label);
    if (existing) {
      existing.items.push(...g.items);
    } else {
      const sec: SectionGroup = { key: `type:${type.label}`, label: type.label, colorVar: type.colorVar, items: [...g.items] };
      byLabel.set(type.label, sec);
      secs.push(sec);
    }
  }
  return secs;
}

// The per-card "why this is here" line: substitute the REAL anchor name into the
// honest relationship label ("comments on this" + "Numbers 13:16" -> "Comments on
// Numbers 13:16"). A label with no "this" (generic / cross-reference) falls back
// to a neutral "Connected to <anchor>" - never a directional guess.
export function reasonText(relLabel: string, anchorName: string): string {
  const l = relLabel.trim();
  let out: string;
  if (/\bthis passage\b/i.test(l)) out = l.replace(/\bthis passage\b/i, anchorName);
  else if (/\bthis\b/i.test(l)) out = l.replace(/\bthis\b/i, anchorName);
  else out = `Connected to ${anchorName}`;
  return out.charAt(0).toUpperCase() + out.slice(1);
}

// Group items by commentator, preserving first-seen order (groupLinks has already
// sorted within a category by strength then name, so a commentator's comments
// arrive contiguous). Lets the UI fold N comments from one author (Rashi a dozen
// times on a daf) into a single expandable bundle instead of N identical cards.
export function bundleByCommentator(
  items: Item[],
): Array<{ commentator: string; commentatorHe: string | null; items: Item[] }> {
  const order: string[] = [];
  const byName = new Map<string, { commentator: string; commentatorHe: string | null; items: Item[] }>();
  for (const it of items) {
    let g = byName.get(it.commentator);
    if (!g) {
      g = { commentator: it.commentator, commentatorHe: it.commentatorHe, items: [] };
      byName.set(it.commentator, g);
      order.push(it.commentator);
    }
    g.items.push(it);
  }
  return order.map((name) => byName.get(name)!);
}

// A commentary ref has an extra segment after the verse ("Rashi on X 15:38:1");
// strip it to the verse-level work ref. A verse-level ref (Targum) is left as-is.
export function commentatorVerseRef(l: SourceLink): string {
  const ref = l.ref;
  if (!/ on /.test(ref)) {
    const m = ref.match(/^(.*\d+[ab])(?::\d+)+$/);
    if (m) return m[1];
  }
  const anchorColons = (l.anchorRef.match(/:/g) ?? []).length;
  const refColons = (ref.match(/:/g) ?? []).length;
  return refColons > anchorColons ? ref.replace(/:\d+$/, "") : ref;
}

export function verseShort(ref: string): string {
  const m = ref.match(/(\d+:\d+)\s*$/);
  return m ? m[1] : ref;
}

// A short, plain-text snippet of the selected verse's OWN text, read straight off
// the rendered verse (Hebrew preferred, English fallback) - never a fetch, never
// HTML. Used to make the selected pasuk recognizable in the Explore header.
export function verseSnippet(verseEl: HTMLElement): { text: string; lang: "he" | "en" } {
  const he = (verseEl.querySelector(".vhe")?.textContent ?? "").replace(/\s+/g, " ").trim();
  const en = (verseEl.querySelector(".ven")?.textContent ?? "").replace(/\s+/g, " ").trim();
  const lang: "he" | "en" = he ? "he" : "en";
  const src = he || en;
  if (!src) return { text: "", lang };
  const words = src.split(" ");
  const text = words.length > 8 ? words.slice(0, 8).join(" ") + " ..." : src;
  return { text, lang };
}

export function itemLabel(it: { commentator: string; ref: string }): string {
  if (/ on /i.test(it.ref)) return it.commentator;
  const m = it.ref.match(/(\d+[ab]|\d+:\d+)\s*$/);
  if (m && !/\d/.test(it.commentator)) return `${it.commentator} ${m[1]}`;
  return it.commentator;
}

// Display-only fix for KNOWN Sefaria ref typos (their data carries a handful).
// This never alters source TEXT - only these specific, confirmed misspellings in
// a ref LABEL we surface. A conservative allowlist, deliberately not a spellchecker
// (which could "correct" legitimate transliterations).
const REF_TYPOS: ReadonlyArray<readonly [RegExp, string]> = [[/\bSciptures\b/g, "Scriptures"]];
export function tidyRefLabel(s: string): string {
  let out = s;
  for (const [re, fix] of REF_TYPOS) out = out.replace(re, fix);
  return out;
}

// When a section lists several PASSAGES of the same work (same visible title -
// e.g. two Divrei Emet teachings on one pasuk), the bare title alone makes the
// cards look like accidental duplicates. Give each colliding card a short
// disambiguator: the part of its ref that is unique once the shared leading
// segments are dropped ("Korach 12" vs "A Collection on Scriptures 5"). Only
// same-title groups of 2+ get one; everything else returns nothing.
export function disambiguateSameWork<T extends { commentator: string; ref: string }>(items: T[]): Map<T, string> {
  const out = new Map<T, string>();
  const byLabel = new Map<string, T[]>();
  for (const it of items) {
    const k = itemLabel(it);
    const g = byLabel.get(k);
    if (g) g.push(it);
    else byLabel.set(k, [it]);
  }
  for (const group of byLabel.values()) {
    if (group.length < 2) continue;
    const segs = group.map((it) => it.ref.split(/,\s*/));
    const minLen = Math.min(...segs.map((s) => s.length));
    let p = 0;
    while (p < minLen - 1 && segs.every((s) => s[p] === segs[0][p])) p++;
    group.forEach((it, gi) => {
      const tail = segs[gi].slice(p).join(", ").trim();
      if (tail) out.set(it, tidyRefLabel(tail));
    });
  }
  return out;
}

function sattribEl(t: VerifiedText): HTMLElement {
  const parts: string[] = [];
  if (t.heVersionTitle) parts.push(t.heVersionTitle);
  if (t.enVersionTitle) parts.push(t.enVersionTitle);
  const a = el("div", "sattrib");
  if (parts.length) a.textContent = `Source: ${parts.join(" / ")}`;
  return a;
}

function note(text: string): HTMLElement {
  return el("div", "snote", text);
}

// The language-filter transparency note: a div.langnote with the explainer text and a
// "Show all" escape hatch back to Default. Shared shape for the three filter notes
// (verse source list, Explore verse branch, Explore source branch); each caller passes
// its own wording, click handler, and optional modifier class (the Explore notes add
// "explore-langnote"), then prepends/appends the returned node where it belongs.
function langNote(text: string, onShowAll: () => void, extraClass = ""): HTMLElement {
  const node = el("div", extraClass ? `langnote ${extraClass}` : "langnote");
  node.append(el("span", "langnote-txt", text));
  const showAll = el("button", "langnote-btn", "Show all");
  showAll.type = "button";
  showAll.setAttribute("aria-label", "Show all sources (switch back to Default)");
  showAll.addEventListener("click", onShowAll);
  node.append(showAll);
  return node;
}

function loadingRow(): HTMLElement {
  return skeletonLines(3); // keeps role="status" (the .cites pending-check reads it)
}

// Shape guard for a persisted side-path (store.data is opaque `unknown`).
function isSidePath(d: unknown): d is SidePath {
  return (
    typeof d === "object" &&
    d !== null &&
    typeof (d as SidePath).id === "string" &&
    Array.isArray((d as SidePath).path) &&
    (d as SidePath).path.every((p) => typeof p === "string") &&
    typeof (d as SidePath).label === "string"
  );
}
